/**
 * Main Window. Handles all interactions with a window
 */

import { getOrCreateMainLogger } from "../logger/main";
import { download, downloadGoLang } from "./download";
import { mainBus } from "./MainBus";
import { iab } from "./IAB";
import defaultWindowOptions from "../permissions/defaultWindowOptions";
import { permissionsManager } from "./PermissionsManager";
import { appDataPreloadsPath } from "./appDataFolder";
import {
	getFilenameFromURL,
	getURL,
	isSameDomain,
	isValidHost,
	isValidURL,
	isValidProtocol,
	retry,
	safeStringify as stringify,
	isWindows,
	tryCatchNotify,
	asInteger,
	ensureBoundsHaveIntegerValues,
	validateBoundsCanBeWellFormed,
} from "../util";
import { mainStore, windowsIndex } from "./mainStore";
import { BrowserViews } from "./BrowserViews";
import { enableContextMenu } from "./contextMenu";
import { affinityManagerSingleton as affinityManager } from "./AffinityGroupManager";
import { openUrlWithBrowser } from "./MainSystem";
import { calculateWindowTitleBarUrl } from "./preloads";
import { addWindowToWindowStream } from "./WindowEventStream";
import {
	Manifest,
	Preload,
	WebContentsRecord,
	AnnotatedBrowserWindowOptions,
	StandardPromise,
	WebContentsId,
	SpawnResponseFSA,
	ErrorResponseFSA,
	MainProcessBusEvent,
	WindowBounds,
} from "../types/types";
import fse from "fs-extra";
import path from "path";
import {
	session,
	BrowserWindow,
	BrowserWindowConstructorOptions,
	nativeImage,
	WebContents,
	Menu,
	dialog,
	DidCreateWindowDetails,
	HandlerDetails,
	Notification,
	Event as ElectronEvent,
	Rectangle,
	screen,
} from "electron";
import { EventEmitter } from "events";
import _ from "lodash";
import crypto from "crypto";

/**
 * Launches a devtools WebContents into a dedicated BrowserWindow.
 *
 * Returns a BrowserWindow if a new devtools was opened. Returns null if a devtools was already open.
 *
 */
export const launchDevtools = (
	displayName: string,
	webContents: WebContents,
	onClosed: () => void
): BrowserWindow | null => {
	// If the devtools are already opened then just return;
	if (webContents.isDevToolsOpened()) return null;

	const bw = new BrowserWindow({
		title: `Developer Tools - ${displayName} - ${webContents.getURL()}`,
	});
	webContents.setDevToolsWebContents(bw.webContents);
	webContents.openDevTools({ mode: "detach" });
	bw.addListener("closed", onClosed);
	return bw;
};

/**
 * return cookies from session or empty array
 *
 * @param {Session} session
 * @return {Array} cookies
 */
const getCookies = async (session: any, hostname: any) => {
	const logger = getOrCreateMainLogger();

	let filter = {};
	// Restrict preload cookies to ones whose file domain matches the hostname of the URL being requested.
	filter = { domain: hostname };

	try {
		return await session.cookies.get(filter);
	} catch (err) {
		console.log("Failed to get cookie", err);
		logger.error("Failed to get cookie", err);
		return [];
	}
};

/**
 * builds a Cookie header from the provided session
 *
 * @param {Session} session
 * @return {String} cookie header
 */
const getCookieHeader = async (session: any, hostname: any) => {
	const cookies = await getCookies(session, hostname);
	return cookies.reduce((result: any, cookie: any) => `${result}${cookie.name}=${cookie.value};`, "");
};

/**
 * Used to determine whether we should download a file. Rudimentary caching.
 * We should consider just using the browser's caching.
 * @param {*} url
 */
const isPreloadAlreadyLoaded = (url: string): boolean => !!mainStore.downloads[url];

const getLoadedPreloadInfo = (url: string): Preload => mainStore.downloads[url];

const saveLoadedPreloadInfo = (url: string, info: Preload) => {
	mainStore.downloads[url] = { ...info };
};

const logger = getOrCreateMainLogger();

let movingWindow: null | string;
let resizingWindow: null | string;
let resizeEdges: string[] = [];

const WINAPI = {
	// https://wiki.winehq.org/List_Of_Windows_Messages
	WM_ENTERSIZEMOVE: 561,
	WM_EXITSIZEMOVE: 562,
};

const selectionMenu = Menu.buildFromTemplate([{ role: "copy" }, { type: "separator" }, { role: "selectAll" }]);

const inputMenu = Menu.buildFromTemplate([
	{ role: "undo" },
	{ role: "redo" },
	{ type: "separator" },
	{ role: "cut" },
	{ role: "copy" },
	{ role: "paste" },
	{ type: "separator" },
	{ role: "selectAll" },
]);

/**
 * gets the Operating System handle for the Window (Windows only)
 */
const getNativeWindowHandle = (bw: BrowserWindow) => {
	let handleBuffer = bw.getNativeWindowHandle();
	// Note: only works on windows. On other operating systems, must check if Little or Big Endian and use rereadInt32LE/BE appropriately.
	return handleBuffer.readInt32LE().toString(16).toUpperCase();
};

/**
 * Creates a new Electron window. This creates a MainWindow object that FEA uses and
 * then initializes it with a new BrowserWindow.
 *
 * createWindow is always called from Main. It is called once to start the app, by
 * creating Finsemble itself (the SystemLauncher as specified in the manifest's startup_app). From
 * then on, it is called when a request is received from Finsemble to spawn a new window via
 * createWindowRequest().
 *
 * Also see createWindowFromBrowserWindow() which is called from window.open handlers to attach
 * a MainWindow to an already existing BrowserWindow.
 */
export const createWindow = async (
	params: AnnotatedBrowserWindowOptions,
	manifest: Manifest
): StandardPromise<MainWindow> => {
	logger.info(`createWindow() ${params.name}`);

	params.permissions = permissionsManager.reconcilePermissions(
		permissionsManager.systemPermissions,
		params.permissions
	);

	params.trustedPreloads = manifest.finsemble.trustedPreloads;

	try {
		const startupAppUrl = manifest.startup_app.url;
		const startupURL = new URL(startupAppUrl);
		params.finsembleDomain = startupURL.hostname;
	} catch (e) {}

	if (!params.execJSWhitelist) params.execJSWhitelist = [];

	const mainWindow = new MainWindow(params, manifest);
	logger.info(`Initializing window ${mainWindow.windowName}.`);
	const { err } = await mainWindow.initialize();
	if (err) return { err };
	logger.log(`Initialized window ${mainWindow.windowName}.`);
	return { data: mainWindow };
};

/**
 * Handles a request to create a window for an application
 */
export const createWindowRequest = async (
	data: any,
	manifest: Manifest
): Promise<SpawnResponseFSA | ErrorResponseFSA> => {
	const { name } = data;
	// wait for application to finish being created before creating the window.
	logger.info(`createWindowRequest ${name}`);

	try {
		const { err, data: newWindow } = await createWindow(data, manifest);
		if (err || !newWindow) {
			return {
				type: "ErrorResponse",
				payload: {
					err: err ?? "No response from createWindow()",
				},
			};
		}
		logger.info(`Window created: ${newWindow.windowName}`);
		return {
			type: "SpawnResponse",
			payload: {
				windowName: newWindow.windowName,
			},
		};
	} catch (err) {
		const error = `createWindowRequest error creating window ${name} : ${err}`;
		console.log(error);
		logger.error(error);
		return {
			type: "ErrorResponse",
			payload: {
				err: error,
			},
		};
	}
};

/**
 * The `preload` param should come from Finsemble core as [{url:string}]. This function
 * converts this to a string[]. It also converts undefined to an empty []. It is also
 * backward compatible with a single string.
 *
 * Finsemble core seems to pass an empty string as a second bogus preload. This is filtered out.
 */
const getURLListFromPreloadParam = (preload?: { url: string }[] | string): string[] => {
	// If preload doesn't exist then make it an empty array
	const preloadExists = preload ?? [];
	// If it exists but it's a string then convert to an array of one Preload (this should never happen anymore)
	const preloadConvertedFromString = typeof preloadExists === "string" ? [{ url: preloadExists }] : preloadExists;
	// If it exists but it's an object then convert to an array (during window.open the array can become an object)
	const preloadConvertedFromObject = Array.isArray(preloadConvertedFromString)
		? preloadConvertedFromString
		: Object.values(preloadConvertedFromString);
	// Extract the `url`s from the Preload objects to make them plain old strings
	const preloads: string[] = preloadConvertedFromObject.map((p: any) => p.url);
	return preloads.filter((p) => p !== "");
};

/**
 * Converts electron bounds into bounds that finsemble can use. The key difference is that
 * electron uses x/y, and finsemble uses left/top.
 * @param {WindowBounds} bounds
 */
const convertBoundsInfo = (bounds: any) => {
	bounds.left = bounds.x;
	bounds.top = bounds.y;
	bounds.bottom = bounds.top + bounds.height;
	bounds.right = bounds.left + bounds.width;
	return bounds;
};

/**
 * Download a preload into a folder on the local filesystem.
 *
 * Files are downloaded once per session. FEA does not check for the existence
 * of already downloaded files when it restarts. It assumes they are stale, regardless
 * of the http caching directives that are received from their hosts.
 */
export const downloadPreloadFile = async (url: string, remoteModuleFolderPath: string): StandardPromise<string> => {
	// MD5 hash + filename has virtually no chance of collision
	const hash = crypto.createHash("md5").update(Buffer.from(url).toString()).digest("hex");
	const fileName = `${hash}-${path.basename(url)}`;
	logger.info(`Downloading preload ${url} to ${fileName}`);

	if (!url) {
		const error = "downloadPreloadFile() no url provided";
		logger.error(error);
		return { err: error };
	}
	const alreadyLoaded = isPreloadAlreadyLoaded(url);
	if (alreadyLoaded) {
		// Make sure that if the file is already downloaded, it gets into the preload list.
		const data = getLoadedPreloadInfo(url).fileName;
		if (!data) {
			const error = `downloadPreloadFile() No file found for url ${url}`;
			logger.error(error);
			return { err: error };
		}
		logger.info(`Preload ${url} already loaded at ${fileName}`);
		return { data };
	}
	const Cookie = await getCookieHeader(session.defaultSession, new URL(url).hostname);
	const downloadOptions = {
		logger,
		headers: Cookie ? Cookie : undefined,
		retries: 3,
	};

	try {
		const { data: preloadFileData } = await download(url, downloadOptions);
		logger.debug(`Preload ${url} successfully downloaded to memory`);

		const preloadFilePath = path.join(remoteModuleFolderPath, fileName);
		logger.debug(`Writing preload ${url} to ${fileName}`);

		// Retry the write
		// retry(() => fse.outputFileSync(preloadFilePath, preloadFileData));
		retry(() => {
			logger.debug(`RETRY: Writing preload ${url} to ${fileName}`);
			fse.outputFileSync(preloadFilePath, preloadFileData);
			logger.debug(`RETRY: Wrote preload ${url} to ${fileName}`);
		});
		logger.debug(`Wrote preload ${url} to ${fileName}`);

		saveLoadedPreloadInfo(url, {
			url,
			fileName: preloadFilePath,
		});

		logger.info(`Preload ${url} successfully downloaded to ${fileName}`);
		return { data: preloadFilePath };
	} catch (err: any) {
		const error = `Error downloading preload ${url} to ${fileName}: "${err}""`;
		logger.error(error);
		return { err: error };
	}
};

/**
 * Checks if the preload is FSBL.js and is running on the same domain
 */
const isFSBLCorePreload = (url: string, finsembleDomain: string): boolean => {
	const fsblCorePreloads = ["FSBL.js"];
	let currentURL;
	try {
		currentURL = new URL(url);
	} catch (err) {
		return false;
	}

	if (currentURL?.hostname !== finsembleDomain) return false;
	return fsblCorePreloads.some((name) => name === getFilenameFromURL(url));
};

/**
 * Our preload security code was implemented incorrectly.
 * We should always allow preloads that are specified in an app's config/appd.
 * We should always allow "required" preloads (e.g. e2o, FSBL, windowTitleBar)
 * We should disallow untrusted apps from updating config/appd for other apps.
 * We should disallow untrusted apps from specifying a preload on spawn for a cross-domain url.
 *
 * Until we can implement these requirements we're simply going to allow all preloads.
 *
 * MainWindow.spec.js will need to be re-implemented when this is fixed.
 *
 * See Kris West's comments in this PR about which of the 4 are currently implemented and where
 * https://github.com/ChartIQ/finsemble-mono/pull/2654
 */
const isPreloadAllowed = (url: string): string | null => {
	//const preloadsAllowed = permissionsManager.checkPermission(mainWindow, "Window.webPreferences.preload");
	//if(!preloadsAllowed) return `Permission: "Window.webPreferences.preload" - Preloads are not allowed for this window`;
	if (!isValidURL(url)) return `Not a valid url "${url}"`;
	return null;
};

/**
 * Trusted preloads can be set in the Manifest as `finsemble.trustedPreloads`
 *
 * FSBL core preloads are automatically considered to be trusted.
 */
const isTrustedPreload = (url: string, mainWindow: MainWindow): boolean => {
	const fileName = getFilenameFromURL(url);

	return (
		mainWindow.trustedPreloads.includes(url) ||
		mainWindow.trustedPreloads.includes(fileName) ||
		isFSBLCorePreload(url, mainWindow.finsembleDomain)
	);
};

/**
 * Download list of preload urls. The downloads are cached in the filesystem.
 * This function resolves to a list of Preload objects that contain references to the
 * original url, downloaded location, and any errors that occurred, as well as a list of
 * Preload objects to be used for the containing titlebar window (if one is used). The preload
 * list for the titlebar window will contain _only_ FSBL.js.
 *
 * @param urls A list of urls for the preloads requested by the app
 * @param mainWindow The MainWindow reference
 * @returns Resolves to a tuple containing a list of Preload objects, and a list of Preload objects
 * to be used for the containing titlebar window (if one is used)
 */
const downloadPreloadFiles = async (
	urls: string[],
	mainWindow: MainWindow
): Promise<{
	preloadList: Preload[];
}> => {
	mainWindow.logLifecycleStatus("CreateBrowserWindow", `Downloading preload files ${stringify(urls)}`);

	const promises = urls.map((url) => {
		const error = isPreloadAllowed(url);
		return error
			? new Promise<any>((resolve, reject) => {
					reject(error);
				})
			: downloadPreloadFile(url, appDataPreloadsPath());
	});

	const results = await Promise.allSettled(promises);

	const preloadList: Preload[] = results.map((result, index) => {
		const url = urls[index];
		if (result.status === "fulfilled") {
			return {
				fileName: result.value.data,
				error: result.value.err,
				url,
				trusted: isTrustedPreload(url, mainWindow),
			};
		} else {
			return {
				url,
				error: result.reason,
			};
		}
	});

	mainWindow.logLifecycleStatus("CreateBrowserWindow", `Downloaded Preload Files`);
	return {
		preloadList,
	};
};

/**
 * Recursively spins through the options used to create a window and sanitizes the object.
 * If a browserWindow is created with an object that has a function as one of its values,
 * you end up with deep electron errors that are really hard to surface.
 * @param {*} bwOptions
 */
const sanitizeBWConstructorOptions = (
	bwOptions: AnnotatedBrowserWindowOptions,
	objectPath = "",
	level = 0
): AnnotatedBrowserWindowOptions => {
	let returnValue = { ...bwOptions };

	if (level >= 10) {
		logger.error(
			`sanitizeBWConstructorOptions was called too many times.  Check for circular bwOptions in ${objectPath}`
		);
		return returnValue;
	}

	for (let key in returnValue) {
		// imesner; 20220609: sanitizing an array object (or anything with a prototype) breaks prototype methods
		if (Array.isArray(returnValue[key])) {
			continue;
		}
		const typeofValue = typeof returnValue[key];
		switch (typeofValue) {
			/**
			 * You cannot send a function across the wire. This loop sanitizes the constructor parameters.
			 **/

			case "function":
				delete returnValue[key];
				break;
			case "object":
				if (returnValue[key] === null) {
					break;
				}

				// Certain built-in objects are ok.  webContents and top, e.g.
				if (["top", "webContents"].includes(key)) {
					break;
				}

				// recursively sanitize objects so no one accidentally sneaks a function in
				returnValue[key] = sanitizeBWConstructorOptions(returnValue[key], `${objectPath}.${key}`, level + 1);

				break;
			default:
				//do nothing.
				break;
		}
	}
	return returnValue;
};

/**
 * Returns true if this window should be managed (called from window.open handlers).
 *
 * Basically, we don't handle anything we don't recognise as a valid URL or any developer console windows
 */
const shouldManageThisWindow = (url: string): boolean => {
	const aboutUrl = getURL(url);
	if (aboutUrl === null) {
		logger.error(`Failed to create new browser window: Invalid URL ${url}`);
		return false;
	}
	// Bypass if the new window is a dev console (otherwise an extra blank window would launch)
	return !(aboutUrl.protocol === "devtools:" || aboutUrl.protocol === "chrome-devtools:");
};

/**
 * This method adds the appropriate did-fail-reload handler.
 *
 * If a componentMaxReloadAttempts is set in the component config, adds a reload/retry mechanism
 * for the component's webcontents if it fails to load. Otherwise simply logs the failure to FEA logs.
 *
 * This is required as some users sit under heavy infrastructure that can take several minutes
 * to reply to an HTTP request. Without this feature users would only get a log message about a failed load
 * and would have to manually reload or relaunch the app.
 *
 * @param webContents The webcontents to reload
 * @param webContentsRecord The relevant WebContentsRecord
 */
const addDidFailLoadHandler = (webContents: WebContents, webContentsRecord: WebContentsRecord) => {
	const { customData } = mainStore.browserWindowOptions[webContents.id];

	let reloadTimeout = customData?.component?.reloadTimeout ?? 60000;
	let maxReloadAttempts = customData?.component?.maxReloadAttempts ?? 0;

	const { windowName, webContentsName } = webContentsRecord;

	const url = webContents.getURL();

	let reloadTries = 0;
	// reload/retry mechanism to execute if componentMaxReloadAttempts is set
	const doReload = () => {
		if (reloadTries >= maxReloadAttempts) {
			logger.info(
				`addDidFailLoadHandler(): Too many attempts (${maxReloadAttempts}) to reload ${url} on ${windowName}: Stopping retry loop`
			);
			return;
		}
		logger.info(`addDidFailLoadHandler(): "did-fail-load" for ${url} on ${windowName}: retrying`);
		setTimeout(() => webContents.reload(), reloadTimeout);
		reloadTries++;
	};

	webContents.on("did-fail-load", (event, errorCode, errorDescription, validatedURL) => {
		if (maxReloadAttempts) {
			doReload();
		} else {
			logger.error(
				`addDidFailLoadHandler(): "did-fail-load" detected from ${webContentsName}`,
				errorCode,
				errorDescription,
				validatedURL
			);
		}
	});
};

/**
 * When a window.open is caught by setWindowOpenHandler, we don't get access to the associated WebContents
 * until "did-create-window" is received in the parent window (the one that called window.open). Here, we
 * listen of that event in order to finish registration of a window.open event.
 */
const setupWebContentsListeners = (webContents: WebContents, webContentsRecord: WebContentsRecord) => {
	const { manifest, webContentsName } = webContentsRecord;

	/**
	 * Gets the basename of the url; this is essentially the location.origin and location.pathname
	 *
	 * @param url the URL to get the basename for
	 * @returns the basename of the URLs
	 */
	const getURLBasename = (url: string) => url?.split("?")[0].split("#")[0];

	// imesner: Surround with try/catch until we upgrade to fix electron issue.  Remove when upgraded.
	// https://github.com/electron/electron/issues/34508
	// PR: https://github.com/electron/electron/pull/34523
	webContents.setWindowOpenHandler(
		(details) =>
			tryCatchNotify(
				() => onSetWindowOpenHandler(webContents, details),
				"An error has occurred while trying to open a window",
				logger
			) ?? { action: "deny" }
	);
	webContents.on("did-create-window", (bw: BrowserWindow, details: DidCreateWindowDetails) =>
		onDidCreateWindow(webContents, manifest, bw, details)
	);
	webContents.on("did-fail-provisional-load", (event, errorCode, errorDescription, validatedURL) => {
		logger.error(
			`did-fail-provisional-load detected from ${webContentsName}`,
			errorCode,
			errorDescription,
			validatedURL
		);
	});

	webContents.on("render-process-gone", (event, details) => {
		const { exitCode, reason } = details;
		logger.error(`render-process-gone detected from ${webContentsName} exit code = ${exitCode}`, reason);
		if (manifest["finsemble-electron-adapter"]?.forceCloseGone) {
			const { mainWindow: win } = windowsIndex.byWebContentsId[webContents.id];

			logger.error(`render-process-gone closing window: ${win.windowName}`);

			// set isClosing to true for all browserViews to prevent "onbeforeunload" from being triggered. When
			// unresponsive or crashed, the "onbeforeunload" event throws and error and hangs.
			win.browserViews.setAllIsClosing(true);
			win.close();

			mainBus.sendEvent("systemEvent.window-force-closed", {
				windowName: win.windowName,
				cause: "gone",
				exitCode,
				reason,
			});
		}
	});

	webContents.on("unresponsive", () => {
		logger.error(`unresponsive detected from ${webContentsName}`);
		if (manifest["finsemble-electron-adapter"]?.forceCloseUnresponsive) {
			const { mainWindow: win } = windowsIndex.byWebContentsId[webContents.id];

			logger.error(`unresponsive closing window: ${win.windowName}`);

			// set isClosing to true for all browserViews to prevent "onbeforeunload" from being triggered. When
			// unresponsive or crashed, the "onbeforeunload" event throws and error and hangs.
			win.browserViews.setAllIsClosing(true);
			win.close();

			mainBus.sendEvent("systemEvent.window-force-closed", { windowName: win.windowName, cause: "unresponsive" });
		}
	});

	webContents.on("preload-error", (event, preloadPath, error) => {
		logger.error(`preload-error detected from : ${webContentsName} loading preload: ${preloadPath}`, error);
	});

	webContents.on("will-navigate", (e, urlToNavigate) => {
		// The finsemble titlebar is loaded after the webContents listeners added.
		// That means current handler will be invoked for all windows with titlebar.
		// The next check prevents redirection to default browser if component has "openLinksInExternalBrowser: true"
		if (urlToNavigate.endsWith("finsemble/ui-components/WindowTitleBar/index.html")) return;

		// Check to see if we are loading the same url (we assume that if the basename matches, then we do not load
		// a new browser window)
		//
		// Get the current URL
		const currentURL = webContents.getURL();
		//
		if (getURLBasename(urlToNavigate) === getURLBasename(currentURL)) {
			return;
		}

		onWillNavigateHandler(webContents, mainStore.browserWindowOptions[webContents.id], e, urlToNavigate);
	});

	addDidFailLoadHandler(webContents, webContentsRecord);
};

/**
 * We must keep track of all Electron WebContents objects that are created. WebContents' are created in numerous ways:
 * - spawning a window (`new BrowserWindow()`)
 * - adding a browser view (`new BrowserView()`)
 * - intercepting window.open calls (setWindowOpenHandler)
 */
export const registerNewWebContents = (
	webContents: WebContents,
	webContentsRecord: WebContentsRecord,
	browserWindowOptions: AnnotatedBrowserWindowOptions
) => {
	mainStore.webContents[webContents.id] = webContentsRecord;
	mainStore.browserWindowOptions[webContents.id] = browserWindowOptions;
	setupWebContentsListeners(webContents, webContentsRecord);
};

const generatePair = (str: string) => {
	const pair = str.trim().split("=");
	if (pair.length == 2) return [[pair[0], pair[1]]];
	if (pair.length == 1) return [[pair[0], true]];
	return [];
};

const maybeFalse = (str: string) => (str === "false" ? false : undefined);
const parseBool = (str: string) => (str === "true" ? true : maybeFalse(str));

// If the feature should be an int, then parse it if it exists and isn't null
const maybeInt = (str?: string) => (str && str !== "null" ? parseInt(str) : undefined);
const maybeFloat = (str?: string) => (str && str !== "null" ? parseFloat(str) : undefined);
const maybeBool = (str?: string) => (str && str !== "null" ? parseBool(str) : undefined);

/**
 * window.open() optionally accepts "features" as a comma separated name/value pair list in the third parameter.
 * If such features are provided by the developer then we let them override the bwOptions (that would otherwise
 * only be settable via childWindowOptions).
 */
const processWindowOpenFeatures = (
	bwOptions: AnnotatedBrowserWindowOptions,
	features?: string
): AnnotatedBrowserWindowOptions => {
	if (!features) return bwOptions;

	const pairs: Record<string, any> = Object.fromEntries(features.split(",").flatMap(generatePair));

	/**
	 * window.open()'s standardized features are: "left","top","width","height" as well as
	 * synonyms: "screenX", "screenY", "innerWidth", "innerHeight"
	 */

	bwOptions.x = maybeInt(pairs.x ?? pairs.left ?? pairs.screenX) ?? bwOptions.x;
	bwOptions.y = maybeInt(pairs.y ?? pairs.top ?? pairs.screenY) ?? bwOptions.y;
	bwOptions.width = maybeInt(pairs.width ?? pairs.innerWidth) ?? bwOptions.width;
	bwOptions.height = maybeInt(pairs.height ?? pairs.innerHeight) ?? bwOptions.height;

	/**
	 * Additional features that we support through window.open() (since Electron will pass them all)
	 */
	bwOptions.opacity = maybeFloat(pairs.opacity) ?? bwOptions.opacity;
	bwOptions.alwaysOnTop = maybeBool(pairs.alwaysOnTop) ?? bwOptions.alwaysOnTop;
	bwOptions.backgroundColor = pairs.backgroundColor ?? bwOptions.backgroundColor;

	bwOptions.title = pairs.title ?? undefined;

	return bwOptions;
};

/**
 * Compiles BrowserWindowOptions for a child window that is about to be spawned (via intercepting `window.open`). The
 * returned options are derived from the parent with just a few members overridden such as url.
 */
const getBWOptionsForWindowOpen = (
	parentWebContentId: number,
	url: string,
	frameName: string,
	features?: string
): Record<string, any> | null => {
	let bwOptions = JSON.parse(JSON.stringify(mainStore.browserWindowOptions[parentWebContentId]));

	if (!bwOptions) {
		const error = `window.open: BrowserViewOptions not found for parent id ${parentWebContentId}. Allowing url ${url}`;
		logger.error(error);
		return null;
	}

	// Remove the parent's URL so that workspaces don't restore our new window with the wrong URL.
	delete bwOptions.customData.window.url;
	// Prevent the parent's title from overriding this child window's
	delete bwOptions.customData.component.displayName;

	/**
	 * The parent can dictate what is allowed or prevented through the `childWindowOptions` config.
	 *
	 * options === childWindowOptions (initially). That means that the initial set of options is
	 * whatever the parent was created with.
	 * The dev on the other end can pass in whatever they want into childWindowOptions.window. Many
	 * of those properties are actual browserWindowOptions (e.g., autoShow). We want to make sure
	 * that any explicitly passed parameters to childWindowOptions.window get applied to the object
	 * that is passed into new BrowserWindow.
	 *
	 * Note: we use lodash merge rather than deepmerge because bwOptions.webContent is a circular reference.
	 * deepmerge doesn't handle circular references.
	 */
	let { childWindowOptions } = bwOptions;
	if (childWindowOptions) {
		if (childWindowOptions.window) {
			// Delete securityPolicy and permissions properties
			// User may try to pass them to override our security settings
			delete childWindowOptions.window.securityPolicy;
			delete childWindowOptions.window.permissions;
			delete childWindowOptions.window.options?.securityPolicy;
			delete childWindowOptions.window.options?.permissions;

			bwOptions = _.merge(_.cloneDeep(bwOptions), childWindowOptions.window, childWindowOptions.window.options);
			// Converts `autoShow` to `show`, which the launcher usually does for us. The child window will pick up the `show` value from the parent, which needs to be overwritten by any value set in `childwindowoptions.window.options`.
			bwOptions.show = bwOptions.autoShow ?? bwOptions.show ?? true;
		}
		bwOptions.customData = _.merge(_.cloneDeep(bwOptions.customData), childWindowOptions);
	} else {
		// childWindowOptions should never be undefined.
		logger.error("onSetWindowOpenHandler() window.open received without childWindowOptions.");
	}

	const randomNumber = Math.trunc(Math.random() * 10000000);
	const windowName = `window-open-finsemble-${randomNumber}`;

	bwOptions.name = windowName;

	bwOptions = processWindowOpenFeatures(bwOptions, features);

	const validFrameName = (name: string) => name !== "" && name !== "null";

	bwOptions.webContentsName = validFrameName(frameName) ? frameName : windowName;

	// This is the URL that the dev-user intended to open. `window.open("https://google.com").
	bwOptions.contentURL = bwOptions.url = url;

	// FEA emits a weird error if we leave this icon when opening Menus
	delete bwOptions.icon;

	// Prevents any invalid options from being passed to the BrowserWindow constructor
	bwOptions = sanitizeBWConstructorOptions(bwOptions);

	return bwOptions;
};

/**
 * Parses config and figures out if we should render the content in the browserWindow or a
 * browserView.
 */
const isTitlebarWindow = (browserWindowOptions: AnnotatedBrowserWindowOptions) => {
	const windowManager = browserWindowOptions?.customData?.foreign?.components?.["Window Manager"] ?? {};
	return (windowManager.FSBLHeader && windowManager.titlebarType === "browserview") || false;
};

/**
 * Comes up with a display name for the WebContents based on what type of BrowserWindow it is.
 */
const deriveWebContentsName = (windowName: string, isTitlebarWindow: boolean): string => {
	if (isTitlebarWindow) return `(TitleBar) ${windowName}`;
	return windowName;
};

/**
 * We intercept window.open calls (see below) in order to manage those windows. This function
 * associates the WebContents of a newly opened window with FEA's internal tracking mechanisms.
 *
 * Note that registerWindowOpen must remain synchronous because it is called from Electron's
 * window.open handlers which are synchronous (because window.open itself is synchronous!)
 */
const registerWindowOpen = (
	bwOptions: AnnotatedBrowserWindowOptions,
	webContents: WebContents,
	bw: BrowserWindow,
	manifest: Manifest,
	windowOptions: Electron.BrowserViewConstructorOptions
) => {
	const windowName = bwOptions.name;

	let preloadURLs = getURLListFromPreloadParam(bwOptions.preload);

	const needsTitlebar = isTitlebarWindow(bwOptions);

	// If we were supposed to be a title bar shell (i.e. not a ui-component), then we do the next best thing and inject
	// the windowTitleBar preload to get a usable header. We use the same windowTitleBar that the parent
	// window uses.
	if (needsTitlebar) {
		// There should always be a windowTitleBarUrl, but just in case there isn't then use the default
		preloadURLs.push(
			calculateWindowTitleBarUrl(bwOptions.windowTitleBarUrl ?? manifest.finsemble.windowTitleBarUrl, "js")
		);
	}

	// `bwOptions.preloads` will be the same as the parent unless overridden by childWindowOptions
	// Since the `new-window` event response must be returned synchronously, there is no
	// time to officially download any new preloads.
	// loaded preload files
	const preloads = preloadURLs.map((preloadUrl) => {
		const { fileName } = mainStore.downloads[preloadUrl];
		return {
			fileName,
			error: null,
			url: bwOptions.url,
			trusted: true,
		};
	});

	// MainWindows from window.open cannot ever be a browserview window (yet)
	bwOptions.isTitlebarWindow = false;

	/**
	 * Because this BrowserWindow was already created with window.open, we have to go around the way
	 * things normally work (e.g., spawn). This method will make sure that the BrowserWindow is
	 * assigned to a MainWindow. Doing this ensures that the call to `getWindowInfoSync` in
	 * render/Window.js won't fail and hang up the render process.
	 */

	const mainWindow = new MainWindow(bwOptions, manifest);

	const webContentsRecord = {
		id: webContents.id,
		manifest,
		isTitlebarWindow: bwOptions.isTitlebarWindow,
		windowName,
		webContentsName: bwOptions.webContentsName,
		isView: false,
		preloads: preloads,
		contextIsolation: bwOptions.webPreferences.contextIsolation,
	};

	mainWindow.finishBrowserWindowAssignment(bw, webContents, webContentsRecord, bwOptions);

	/**
	 * Send an event to Finsemble's window service with information about this new window.
	 * Finsemble's window service will wrap the window and begin to manage it.
	 * try/catch here to make sure we have every single log possible if window.open fails
	 **/
	try {
		mainBus.sendEvent("systemEvent.new-window-created", {
			name: windowName,
			options: windowOptions,
		});
	} catch (e: any) {
		logger.error("Error trying to send new-window-created", e.message);
	}
};

/**
 * Handler for will-navigate events, i.e. user navigating through links.
 * Checks config for allowed hosts
 * @param webContents This window's webcontents, used to get page's url for logging
 * @param options BrowserWindowOptions, used for config values
 * @param e electron event
 * @param urlToNavigate from the event
 */
const onWillNavigateHandler = (
	webContents: WebContents,
	options: AnnotatedBrowserWindowOptions,
	e: Electron.Event<Electron.WebContentsWillNavigateEventParams>,
	urlToNavigate: string
) => {
	let allowed = true;
	const componentUrl = webContents.getURL();
	// will-navigate first fires with url of ''
	if (componentUrl.length === 0) {
		allowed = true;
	} else if (options?.allowedNavigationUrls !== undefined) {
		allowed = isValidHost(urlToNavigate, options?.allowedNavigationUrls);
	} else if (!isSameDomain(componentUrl, urlToNavigate)) {
		logger.error(
			`Browser window navigated across domains but declared no options.allowedNavigationUrls.  The default behavior will change in the future that will block this action. From ${componentUrl} To: ${urlToNavigate}`
		);
	}
	if (!allowed) {
		e.preventDefault();
		const msg = `Browser window navigation was blocked. From: ${componentUrl} To: ${urlToNavigate}`;
		logger.error(msg);
		new Notification({ body: msg }).show();
	} else if (options?.openLinksInExternalBrowser) {
		e.preventDefault();
		logger.info(`Launching navigation window in external browser: From: ${componentUrl} To: ${urlToNavigate}`);
		openUrlWithBrowser({ data: urlToNavigate, respond: Function.prototype });
	}
};

/**
 * Handler for Electron's setWindowOpenHandler() callback (which is called on window.open). This allows us
 * to override BrowserWindow options in order to control the properties on the new window.
 *
 * Caveats:
 * - This methodology does not provide direct access to the WebContents or BrowserWindow for the new window. A
 * separate event handler for `did-create-window` is used to obtain that information. This relies on Electron's
 * ordering of events: (1) setWindowOpenHandler callback in opener, (2) web-contents-created on app, (3) did-create-window on opener, (4) preload in renderer
 * - This function must return synchronously. This means that we cannot download new preloads for window.open.
 * - This methodology does not allow the new window to be placed in a BrowserView
 * - This methodology will not run preloads when contextIsolation is enabled
 */
export const onSetWindowOpenHandler = (
	parentWebContents: WebContents,
	details: HandlerDetails
): { action: "deny" } | { action: "allow"; overrideBrowserWindowOptions?: BrowserWindowConstructorOptions } => {
	const { url, features } = details;
	logger.debug("onSetWindowOpenHandler()", url, details);

	// First, check if this is a devtools window, and always allow if so
	if (!shouldManageThisWindow(url)) return { action: "allow" };

	const bwOptions = getBWOptionsForWindowOpen(parentWebContents.id, url, details.frameName, features);

	if (!bwOptions) return { action: "allow" };

	// Check for config allowing access to open the url
	let allowed = true;
	if (bwOptions.allowedWindowOpenUrls !== undefined) {
		allowed = isValidHost(url, bwOptions.allowedWindowOpenUrls);
	} else if (!isSameDomain(parentWebContents.getURL(), url)) {
		logger.error(
			`A new window was opened to a domain that wasn't specified in the app's options.allowedWindowOpenUrls. Finsemble's default behavior will change in the future that will block this action. From ${parentWebContents.getURL()} opened: ${url}`
		);
	}

	//check if this relates to an existing window opened with window.open

	if (
		windowsIndex?.byWebContentsName[bwOptions.webContentsName]?.webContents &&
		!windowsIndex?.byWebContentsName[bwOptions.webContentsName]?.webContents.isDestroyed()
	) {
		//  When a named frame/target is re-used in a window.open call, Electron's default behaviour
		//  will call this windowOpenHandler but will not fire did-create-window afterwards.
		//  (understandable as it didn't creat a window, it rescycled one)
		//  However, it also changes the id/replaces the webContents of the named window -
		//  but as we don't receive it through did-create-window we lose track of the webContents/Window
		//  relationship. Instead we can simply deny the window.open here and navigate the
		//  window to its new URL with the same webContents.
		const existingWebContentsRecord = windowsIndex.byWebContentsName[bwOptions.webContentsName];
		logger.debug(
			`Navigating existing window on window.open rather than allowing Electron to handle it, webContentsId: ${existingWebContentsRecord.webContentsId}, webContentsName: ${bwOptions.webContentsName}, url: ${url}`
		);

		//check if allowed to navigate to requested URL here, if not then it will be denied by next block
		if (allowed) {
			existingWebContentsRecord.webContents.loadURL(url).catch((err: any) => {
				const error = `BrowserWindow.onSetWindowOpenHandler() loadURL error when trying to navigate existiing webContents: ${
					err ? err.message : err
				}`;
				logger.error(error);
			});
			return { action: "deny" };
		}
	}

	if (!allowed) {
		const msg = `Browser window opening window was blocked. From: ${parentWebContents.getURL()} Opened: ${url}`;
		logger.error(msg);
		new Notification({ body: msg }).show();
		return { action: "deny" };
	}

	// If allowed, save the bwOptions so that they can be associated with a MainWindow and the mainStore
	// when we receive the `did-create-window` event. At that point, registerWindowOpen() will
	// be called (which does occur before the renderer preload runs).
	mainStore.unattachedChildWindows[parentWebContents.id] = bwOptions;

	logger.debug(`Completed onSetWindowOpenHandler() ${bwOptions.name}`);
	return { action: "allow", overrideBrowserWindowOptions: bwOptions };
};

export const onDidCreateWindow = (
	parentWebContents: WebContents,
	manifest: Manifest,
	bw: BrowserWindow,
	details: DidCreateWindowDetails
) => {
	const { webContents: childWebContents } = bw;
	// If the new window's id is already registered then do nothing - this event was probably from a normal spawn.
	if (mainStore.webContents[childWebContents.id]) return;
	// Otherwise, look for an unattached child window from a previous setWindowOpenHandler() event
	const parentId = parentWebContents.id;
	const bwOptions = mainStore.unattachedChildWindows[parentId];
	if (!bwOptions) {
		const error = `Unattached child window not found in "did-create-window for parent ${parentWebContents.getURL()} and child ${childWebContents.getURL()}`;
		logger.error(error);
		return;
	}
	delete mainStore.unattachedChildWindows[parentId];
	registerWindowOpen(bwOptions, childWebContents, bw, manifest, details.options);
};

/**
 * Retrieves the url for the browserView. Either it's the contentURL (window.open route)
 * or the url (normal window creation route).
 */
export const getURLFromOptions = (browserWindowOptions: AnnotatedBrowserWindowOptions) =>
	browserWindowOptions.contentURL || browserWindowOptions.url;

/**
 * There is currently no representation of the windowTitleBar html page within finsemble's config
 * so this function is necessary to derive the html entry point for when a window is running with a title bar shell.
 */
const getTitleBarShellURL = (browserWindowOptions: AnnotatedBrowserWindowOptions): string => {
	if (!browserWindowOptions.windowTitleBarUrl) {
		logger.error(
			"getTitleBarShellURL was called with useBrowserView but without a windowTitleBarUrl.  Defaulting to contentURL || url"
		);
		return "";
	}
	const { windowTitleBarUrl } = browserWindowOptions;

	return calculateWindowTitleBarUrl(windowTitleBarUrl, "html");
};

/**
 * There are two entry paths to establishing a MainWindow.
 *
 * (1) spawning
 *        (a) createWindow() is called from an inbound or internal spawn() request.
 *        (b) A MainWindow is constructed from the params/options.
 *        (c) MainWindow.initialize() creates an Electron BrowserWindow.
 *        (d) MainWindow.finishBrowserWindowAssignment() updates FEA's stores and
 *            conducts other housekeeping.
 * (2) window.open (or <a href="..." target="{target}">)
 *        (a) a window.open event is intercepted by one of three means
 *        (b) registerWindowOpen() is called which constructs a MainWindow and associates it
 *            with the WebContents that we intercepted.
 *        (c) MainWindow.finishBrowserWindowAssignment() updates FEA's stores and
 *            conducts other housekeeping.
 */

export class MainWindow extends EventEmitter {
	windowName: string;

	webContentsName: string;

	apiUnsubscribes: (() => void)[] = [];

	movable: boolean = true;

	isMoving: boolean = false;

	// Toggles to true when Finsemble has registered a "close-requested" event. When this is true, automatic window closes are disabled.
	closeRequested: boolean = false;

	url: string;

	// Timestamp for last time a window was moved. Used to suppress extra move event when the OS moves a window.
	lastMove: number = Date.now();

	customData: any;

	permissions: any;

	finsembleDomain: string;

	trustedPreloads: string[];

	affinity: string | boolean;

	originalParams: any;

	partialShapeSet: boolean = false;

	// Boolean that tells us whether to execute code in the `onWindowClosedHandler`.
	// When this gets set to true, we skip code in the closed handler that's set up
	// when the window is created. Instead, we clean up after we close the thing.
	// This ensures a tidy close operation.
	wasClosedByRender: boolean = false;

	useDOMMovement: boolean;

	// This is used by BrowserViews to determine when it is safe for them to load their content.
	// A BrowserView cannot load its content before its BrowserWindow has triggered the dom-ready event.
	domReady: boolean = false;

	myOpts: AnnotatedBrowserWindowOptions;

	isWindowShowing: boolean;

	win!: BrowserWindow;

	zoomInterval: ReturnType<typeof setTimeout> | undefined;

	webContentsId: WebContentsId = -1;

	static hookWindowMessageDisplayed: any;

	isRestoring: boolean = false;

	/**
	 * When windows are maximized or unmaximized, an animation effect triggers a series of resize effects
	 * that can cause problems with Finsemble's window service, and which Finsemble's window service can
	 * interfere with, causing the animation effect to look clunky.
	 * This variable set to true after a call to maximize() or unmaximize() and then set to false
	 * when a maximize or unmaximize event is received. resize events that are received while
	 * this is in effect are ignored.
	 */
	inMaximizeAnimation: boolean = false;

	isResizing: boolean = false;

	setBoundsCalled: boolean = false;

	titleBarHeight: number = 0;

	location: any;

	movingObj: any;

	lastResizeTime: number | undefined;

	lastSizeMoveLoc: { left: any; top: any; height: any; width: any; changeType: number } | undefined;

	startMoveWindowLocation!: WindowBounds;

	startMoveMouseLocation: Electron.Point = { x: 0, y: 0 };

	details: AnnotatedBrowserWindowOptions;

	devtools: BrowserWindow | null = null;

	// This gets set whenever setDeveloperTools is called programmatically, to prevent the devtools-opened
	// handler from running (we only want that handler running when the end user presses an accelerator)
	dontRedirectDevToolsOnce: boolean = false;

	destroyWindowTimer: ReturnType<typeof setTimeout> | undefined;

	normalBounds: any;

	resizeEventTimeout: ReturnType<typeof setTimeout> | undefined;

	hasView: boolean = false;

	preloadList: Preload[] = [];

	manifest: Manifest;

	browserViews: BrowserViews = new BrowserViews(this);

	/** Set to true if the window only contains a titlebar (i.e. it is the titlebar portion of a browserview window
	 * and needs to load the titlebar URL). Note that apps use injected titlebars unless their
	 * `foreign.components.Window Manager.titlebarType` is set to "browserview" and do NOT have
	 * `isTitlebarWindow` set to true.
	 * MB 7/2022 `titlebarType` now defaults to "browserview" instead of "injected
	 */
	isTitlebarWindow: boolean;

	/**
	 * Declares whether or not this represents a hidden affinity surrogate window,
	 * which is used to launch child windows under the same affinity.
	 */
	isAffinitySurrogate: boolean;

	defaultAlwaysOnTopLevel: string | null;

	constructor(params: AnnotatedBrowserWindowOptions, manifest: Manifest) {
		super();
		this.bindFunctions();
		this.manifest = manifest;
		// Need to remove one of these. Windows should probably be tracked by UUID instead of name
		this.windowName = params.name;
		this.url = params.url;
		this.customData = params.customData ?? {};
		this.permissions = params.permissions;
		this.finsembleDomain = params.finsembleDomain;
		this.trustedPreloads = params.trustedPreloads || [];
		this.affinity = params.affinity;
		this.isAffinitySurrogate = params.isAffinitySurrogate ?? false;
		this.originalParams = params;
		// DEPRECATED remove in Finsemble >= 9 (and always useDomBasedMovement)
		//Default to always use DOM based window movement. If the appropriate manifest variable is set
		// and the platform is win32 based (Windows), then set to the configs value
		this.useDOMMovement =
			process.platform !== "win32" || (this.manifest["finsemble-electron-adapter"]?.useDOMBasedMovement ?? true);

		/**
		 * contextIsolation currently defaults to false because Electron has a bug that prevents preloads from
		 * running within window.open() when contextIsolation is true (https://github.com/electron/electron/issues/32136).
		 *
		 * finsemble-electron-adapter.contextIsolation is an undocumented config that allows us to turn it on and off
		 * for development purposes.
		 */
		const contextIsolation = this.manifest["finsemble-electron-adapter"]?.contextIsolation ?? false;

		// Workaround for an electron/chromium issue where the length or width of a window being too large causes all other app windows to flash
		// This seems to happen at around 16400(the exact number varies) for either dimension and at both positive and negative coordinates.
		// Prior to electron 11.2.0 electron would decrease the value often by cutting the front digits off
		// (16000 would become 6000), since this no longer happens we limit max size on window creation if one is not already set.

		// Scalefactor does not modify the maxsize but it does affect the safe point. Unfortunately that means this number may not be safe for above 200% scaling
		// as any the max size * scalefactor needs to be less than around that 16400 number. We can't take scalefactor into account as this issue is trigged on a resize over
		// monitor bounds and we don't currently determine the exact point where a window changes monitor ownership as the OS knows it. Even if we had this information, we would need to
		// change the max window size every time we called setbounds from resize. If the default max sizes are too big the user will need to set a max size in the config or as a spawn param, this is already supported.

		// Currently, we are not updating our bounds to match when the width/height is reduced. getBoundsFromSystem will reflect the correct value and an error will be logged when a value is reduced in setbounds.
		const DEFAULT_MAX_HEIGHT_WIDTH = 8000;

		this.myOpts = Object.assign({}, defaultWindowOptions, params, {
			width: params.defaultWidth ?? params.width ?? 800,
			height: params.defaultHeight ?? params.height ?? 600,
			x: params.defaultLeft ?? params.x ?? 0,
			y: params.defaultTop ?? params.y ?? 0,
			maxWidth: params.maxWidth && params.maxWidth !== -1 ? params.maxWidth : DEFAULT_MAX_HEIGHT_WIDTH,
			maxHeight: params.maxHeight && params.maxHeight !== -1 ? params.maxHeight : DEFAULT_MAX_HEIGHT_WIDTH,
			url: params.url,
			// autoShow for backward compatibility
			show: params.show ?? params.autoShow ?? false,
			icon: params.icon,
			title: params.name,
			frame: params.frame,
			skipTaskbar: params.showTaskbarIcon === false,
			autoHideMenuBar: true,
			acceptFirstMouse: true,
			useBrowserView: params.useBrowserView,
			// Finsemble windows rely on the manifest existing in window options in order to start the router client
			manifest,
			webPreferences: {
				webSecurity: true,
				nodeIntegration: false,
				contextIsolation,
				preload: path.join(__dirname, "e2o.js"), // Preload e2o.js (which will then load all the Finsemble preloads)
				backgroundThrottling: params.backgroundThrottling ?? true,
			},
		});

		/**
		 * Keeping track of the window show() / hide() state. Electron's moveTop() which is used in bringToFront()
		 * will unhide hidden windows. Using Electron's isVisible() is not advised as it works differently depending
		 * on the OS. Its use is also more focused on if the window is in view or not rather than its hidden/shown state
		 *
		 * @see https://www.electronjs.org/docs/api/browser-window#page-visibility
		 */
		this.isWindowShowing = this.myOpts.show;

		/**
		 * Electron AoT default level is "floating" (null). "floating" causes an issue where always on top hides the
		 * window behind all other windows when screen sharing. On Windows we use "normal" to avoid this problem
		 */
		this.defaultAlwaysOnTopLevel = isWindows() ? "normal" : null;

		/**
		 * For a window to be transparent it must obey the following conditions:
		 * BrowserWindow.transparent = true
		 * No background color
		 * Opacity must not be set in any way (VMWare only)
		 *
		 * The window must not have any CSS that sets the background color on the window's body. Note that
		 * finsemble.css includes a body.background declaration which must be overridden.
		 *
		 * On Mac, a window will never be transparent if the background color is initially set. In other words,
		 * you cannot dynamically remove the background color because the original color will stick. This includes
		 * toggling the background color in developer tools.
		 *
		 * On Windows, the background color may be toggled.
		 *
		 * Be careful to test any changes on: Mac, Windows, Windows on VMWare
		 */
		if (this.myOpts.transparent) {
			// Transparency will only work if there is no background color on a window
			delete this.myOpts.backgroundColor;
			// On VMWare, setting opacity in any way causes transparency to fail
			delete this.myOpts.opacity;
		}

		// Obey the isTitlebarWindow and webContentsName settings from params if they exist (such as when sent by window.open)
		// but otherwise derive those settings.
		const shouldBeBrowserviewWindow = this.myOpts.useBrowserView || isTitlebarWindow(this.myOpts);
		this.isTitlebarWindow = this.myOpts.isTitlebarWindow ?? shouldBeBrowserviewWindow;
		this.webContentsName = this.myOpts.webContentsName ?? deriveWebContentsName(this.windowName, this.isTitlebarWindow);
		this.hasView = this.myOpts.useBrowserView || this.isTitlebarWindow;

		this.details = this.myOpts;
	}

	/**
	 *
	 */
	async initialize(): StandardPromise<void> {
		const preloads = getURLListFromPreloadParam(this.originalParams.preload);

		// Make sure all preloads are downloaded, then create an Electron Window
		const { preloadList } = await downloadPreloadFiles(preloads, this);
		this.preloadList = preloadList;

		const preloadListFSBLCoreOnly = preloadList.filter((preload) =>
			isFSBLCorePreload(preload.url, this.finsembleDomain)
		);

		this.logLifecycleStatus("MainWindow.Initialize", `Creating physical browserWindow.`);

		// Get the taskbarIcon for this app. This comes from `icons.taskbarIcon` but previously came from just `icon`.
		const taskbarIcon = this.myOpts.icons?.taskbarIcon?.path ?? this.myOpts.icon;

		// In Electron 9.x+, BrowserWindow throws an error for string paths to `icon`.
		// So remove it before creating the BrowserWindow. We use win.setIcon() to set the actual icon after the BrowserWindow is created.
		// https://github.com/electron/electron/issues/24221
		delete this.myOpts.icon;

		// Andy - 20220713: logging at higher than verbose level fills up the FEA logs
		logger.verbose(`BrowserWindow options for ${this.windowName}, ${stringify(this.myOpts)}`);

		const shouldLoadAffinity = !!this.affinity;
		const windowManager = this.myOpts?.customData?.foreign?.components?.["Window Manager"] ?? {};
		let bw: BrowserWindow;
		const url = this.isTitlebarWindow ? getTitleBarShellURL(this.myOpts) : getURLFromOptions(this.myOpts);

		// imesner: 20220616: electron allows BrowserWindow.loadUrl("C:/path/to/local/file.html")
		// and we've decided to disallow this explicitly.  A guard at the lowest level seemed prudent.
		const allAllowedProtocols = [
			...(this.manifest["finsemble-electron-adapter"]?.allowedBrowserProtocols ?? []),
			"http",
			"https",
			"mailto",
		];

		if (!isValidProtocol(this.myOpts.url, allAllowedProtocols)) {
			const err = `Invalid URL supplied: ${this.myOpts.url}.  See allowedBrowserProtocols configuration.`;
			logger.error(err);
			return { err };
		}

		try {
			// typeguard to allow passing affinity to affinityGroups as a string only type since affinity=false won't make it into this block
			if (shouldLoadAffinity && typeof this.affinity === "string") {
				logger.debug(
					`MainWindow.initialize - Affinity enabled - spawning URL:${url} using affinity ${this.affinity} windowName ${this.windowName}`
				);

				bw = await affinityManager.spawn(this.affinity, url, this.myOpts);
				logger.debug(
					`MainWindow.initialize - Affinity spawn complete - URL:${url} using affinity ${this.affinity} windowName ${this.windowName}`
				);

				if (!bw) {
					throw new Error(`Couldn't create BrowserWindow with affinity group ${this.affinity}`);
				}

				// affinity is disabled, use the standard method to launch windows. Usually windows launched as their own process.
			} else {
				logger.debug(`MainWindow.initialize - Affinity disabled - spawning URL:${url} via standard BrowserWindow`);

				bw = new BrowserWindow(this.myOpts);

				// Here is where the web page is actually loaded
				bw.loadURL(url).catch((err: any) => {
					const error = `BrowserWindow.loadURL error ${err ? err.message : err}`;
					logger.error(error);
				});
			}
		} catch (err: any) {
			const error = `Error creating BrowserWindow in MainWindow.js: ${err.message ? err.message : err}`;
			logger.error(error);
			return { err: error };
		}

		const { webContents } = bw;
		const webContentsRecord = {
			id: webContents.id,
			manifest: this.manifest,
			isTitlebarWindow: this.isTitlebarWindow,
			windowName: this.windowName,
			webContentsName: this.webContentsName,
			isView: false,
			preloads: this.isTitlebarWindow ? preloadListFSBLCoreOnly : preloadList,
			contextIsolation: this.myOpts.webPreferences.contextIsolation,
		};
		logger.debug(
			`MainWindow.initialize - created webContentsRecord and finishing browser window assignment ${this.windowName}`
		);
		this.finishBrowserWindowAssignment(bw, webContents, webContentsRecord, this.myOpts);
		logger.debug(`finished browser window assignment ${this.windowName}`);

		if (this.isTitlebarWindow) {
			this.titleBarHeight = windowManager.titleBarHeight;
			// Always create a view taking up the entire window (minus any titlebar space) This first view will always have id 0;
			// We have several functions that act on the view if it exists, for now act on this first view.
			// When the browser view is within a titlebar shell it's webContentsName assumes the window name
			// (and the parent BrowserWindow gets "TitleBar:" in its webContentsName.)
			this.browserViews.addBrowserView({
				url: getURLFromOptions(this.myOpts),
				webContentsName: this.windowName,
				closeWindowOnWebContentsClose: true,
			});
		}

		if (taskbarIcon) {
			this.logLifecycleStatus(
				"MainWindow.Initialize",
				`Window ${this.myOpts.name} has a taskbar icon. Downloading icon ${taskbarIcon}.`
			);
			try {
				this.setWindowIcon(taskbarIcon);
				this.logLifecycleStatus(
					"MainWindow.Initialize",
					`Window ${this.myOpts.name} taskbar icon downloaded. Proceeding.`
				);
			} catch (err) {
				logger.error(`Failed to set taskbar icon: ${err}`, taskbarIcon);
			}
		}

		/**
		 * Calling setSize() works around an electron bug where different scaling factors of the main display will
		 * cause windows to be created at the wrong size on other monitors when calling `new BrowserWindow`.
		 * [https://github.com/electron/electron/issues/16444]
		 * [https://github.com/electron/electron/issues/10862]
		 *
		 */
		bw.setSize(asInteger(this.myOpts.width), asInteger(this.myOpts.height));

		bw.webContents.once("dom-ready", () => {
			this.domReady = true;
		});

		if (this.myOpts.contextMenu) {
			enableContextMenu(bw.webContents, bw);
		}

		this.logLifecycleStatus(
			"MainWindow.Initialize",
			`Window creation complete ${this.windowName}. WebContentsID: ${this.win.webContents.id}`
		);

		return {};
	}

	/**
	 * Finishes all the initialization of MainWindow that must be done after a BrowserWindow is
	 * assigned. This can get called from two paths: initialize() (when a window is spawned)
	 * or registerWindowOpen() (when a window.open is intercepted).
	 */
	finishBrowserWindowAssignment(
		bw: BrowserWindow,
		webContents: WebContents,
		webContentsRecord: WebContentsRecord,
		browserWindowOptions: AnnotatedBrowserWindowOptions
	) {
		this.win = bw;
		this.webContentsId = webContents.id;
		this.details.nativeWindowHandle = getNativeWindowHandle(bw);

		// Save references to our global stores
		registerNewWebContents(webContents, webContentsRecord, browserWindowOptions);
		windowsIndex.byWebContentsId[webContents.id] = {
			webContentsName: this.webContentsName,
			webContents,
			mainWindow: this,
		};
		windowsIndex.byWebContentsName[webContentsRecord.webContentsName] = {
			webContentsId: webContents.id,
			webContents,
			mainWindow: this,
		};
		windowsIndex.byWindowName[this.windowName] = this;

		// BrowserWindowConstructorOptions doesn't allow alwaysOnTop as an option, so we have to manually set it here
		bw.setAlwaysOnTop(this.myOpts.alwaysOnTop, this.myOpts.alwaysOnTopLevel ?? this.defaultAlwaysOnTopLevel);

		this.setupListeners();
		addWindowToWindowStream(this);
	}

	/**
	 * It's no longer necessary to bind member functions on MainBus listeners since we now do this
	 * generically in setupListeners. These lines can be removed if proven safe
	 */
	bindFunctions() {
		this.sendEventToWindows = this.sendEventToWindows.bind(this);
		this.mouseUp = this.mouseUp.bind(this);
		this.mouseDown = this.mouseDown.bind(this);
		this.userMoveEvent = this.userMoveEvent.bind(this);
		this.userResizeEvent = this.userResizeEvent.bind(this);
		this.moveOrResizeEvent = this.moveOrResizeEvent.bind(this);
		this.windowMouseDown = this.windowMouseDown.bind(this);
		this.windowMoving = this.windowMoving.bind(this);
		this.windowMoved = this.windowMoved.bind(this);
		this.resizeEventOsx = this.resizeEventOsx.bind(this);
		this.getBounds = this.getBounds.bind(this);
		this.getBoundsFromSystem = this.getBoundsFromSystem.bind(this);
		this.getTaskbarTitle = this.getTaskbarTitle.bind(this);
		this.setBounds = this.setBounds.bind(this);
		this.removeBrowserView = this.removeBrowserView.bind(this);
		this.setViewBounds = this.setViewBounds.bind(this);
		this.bringViewToFront = this.bringViewToFront.bind(this);
		this.setIgnoreMouseEvents = this.setIgnoreMouseEvents.bind(this);
		this.setMinimumSize = this.setMinimumSize.bind(this);
		this.setMaximumSize = this.setMaximumSize.bind(this);
		this.setShape = this.setShape.bind(this);
		this.setResizable = this.setResizable.bind(this);
		this.setTaskbarIconOverlay = this.setTaskbarIconOverlay.bind(this);
		this.setTaskbarIcon = this.setTaskbarIcon.bind(this);
		this.setTaskbarTitle = this.setTaskbarTitle.bind(this);
		this.showNativeOpenDialog = this.showNativeOpenDialog.bind(this);
		this.updateOptions = this.updateOptions.bind(this);
		this.getDetails = this.getDetails.bind(this);
		this.getSnapshot = this.getSnapshot.bind(this);
		this.showAt = this.showAt.bind(this);
		this.show = this.show.bind(this);
		this.blur = this.blur.bind(this);
		this.hide = this.hide.bind(this);
		this.close = this.close.bind(this);
		this.maximize = this.maximize.bind(this);
		this.minimize = this.minimize.bind(this);
		this.bringToFront = this.bringToFront.bind(this);
		this.restore = this.restore.bind(this);
		this.isShowing = this.isShowing.bind(this);
		this.disableFrame = this.disableFrame.bind(this);
		this.enableFrame = this.enableFrame.bind(this);
		this.reload = this.reload.bind(this);
		this.showDeveloperTools = this.showDeveloperTools.bind(this);
		this.focus = this.focus.bind(this);
		this.closeRequestedAdd = this.closeRequestedAdd.bind(this);
		this.closeRequestedRemove = this.closeRequestedRemove.bind(this);
	}

	/**
	 * Removes every handler that was added when the window was created.
	 */
	removeAPIHandlers() {
		this.apiUnsubscribes.forEach((unsubscriber) => {
			unsubscriber();
		});
		this.apiUnsubscribes = [];
		logger.info(`API handlers removed for ${this.windowName}`);
	}

	handleWillResizeWithoutDOMMovement(event: ElectronEvent, newBounds: Rectangle) {
		// See note in inMaximizeAnimation description
		if (this.inMaximizeAnimation) return;
		if (this.isRestoring) {
			newBounds = this.win.getNormalBounds();
			this.isRestoring = false;
		}
		if (process.platform === "darwin") {
			this.resizeEventOsx(event, newBounds);
		} else {
			this.userResizeEvent(event, newBounds);
		}
	}

	/**
	 * A debounced resize handler. This exists to partially mitigate an Electron
	 * issue whereby the vertical resize for is not correctly handled. We listen
	 * to resize events and then re-render the browser views (titlebar and content).
	 *
	 * This is a workaround, identified in FIN-2020; Electron has an open issue:
	 * https://github.com/electron/electron/issues/43339
	 *
	 * Related comment:
	 * https://github.com/electron/electron/issues/22174
	 *
	 * TODO: Remove when Electron issue is fixed
	 */
	debouncedResizeHandler = _.debounce(
		() => {
			if (this.isTitlebarWindow) {
				this.browserViews.adjustToParent();
			}
		},
		125,
		{ leading: false, trailing: true }
	);

	handleResizeEvent() {
		// On macOS, calling setBounds while resizing triggers this event but a final event is never triggered causing group resize to break on the mac.
		// So don't do anything on macOS if a setBounds call is received while resizing.
		if (process.platform == "darwin") {
			if (this.isResizing && this.setBoundsCalled) {
				this.setBoundsCalled = false;
			} else {
				this.isResizing = false;
				this.moveOrResizeEvent(1);
			}
			return;
		}
		this.moveOrResizeEvent(1);
	}

	/**
	 * Setup event listeners on the electron window.
	 * When a 'native' event happens, push it out to the rest of the system.
	 */
	setupListeners() {
		this.logLifecycleStatus("MainWindow.setupWindowEventListeners", `Setting up window event listeners.`);

		/**
		 * Handle inbound requests from Finsemble (via the renderers).
		 * A listener is created for each API method that is permissioned for this window.
		 * An accessDenied listener is attached if the permission is not enabled.
		 */
		Object.keys(this.permissions?.Window ?? {}).forEach((name) => {
			const func = (this as any)[name];
			if (!func) return;
			const topic = `${this.windowName}-${name}`;

			// Get a reference to this class's matching member function, and bind it to "this" for event handling
			const handler = permissionsManager.addRestrictedListener(topic, func.bind(this), `Window.${name}`);
			// Store the unsubscriber to remove the handler when the window closes.
			this.apiUnsubscribes.push(handler);
		});

		this.win.webContents.on("devtools-opened", () => {
			this.onDevToolsOpened();
		});
		this.win.on("blur", () => {
			this.sendEventToWindows("blurred", { name: "blur" });
		});
		this.win.on("focus", () => {
			this.sendEventToWindows("focused", { name: "focus" });
		});
		this.win.on("show", () => {
			this.sendEventToWindows("shown", { name: "show" });
		});
		this.win.on("hide", () => {
			this.sendEventToWindows("hidden", { name: "hide" });
		});
		this.win.on("minimize", () => {
			this.sendEventToWindows("minimized", { name: "minimize" });
		});
		//The "this.isTitlebarWindow" cases below fix an issue where view doesn't resize properly when maximizing and restoring from maximize
		this.win.on("maximize", () => {
			this.inMaximizeAnimation = false;
			this.sendEventToWindows("maximized", { name: "maximize" });
			if (this.isTitlebarWindow) this.debouncedResizeHandler();
		});
		this.win.on("restore", () => {
			this.sendEventToWindows("restored", { name: "restore" });
			if (this.isTitlebarWindow) this.debouncedResizeHandler();
		});
		//aerosnap case
		this.win.on("unmaximize", () => {
			this.inMaximizeAnimation = false;
			this.sendEventToWindows("restored", { name: "unmaximize" });
			if (this.isTitlebarWindow) this.debouncedResizeHandler();
		});

		this.logLifecycleStatus("MainWindow.customWindowEvents", `Setting up custom window events.`);
		if (this.win.hookWindowMessage) {
			// When the window's ENTERSIZEMOVE event is thrown, we will call 'mouseDown'.
			this.win.hookWindowMessage(WINAPI.WM_ENTERSIZEMOVE, this.mouseDown.bind(this));
			// When the window's EXITSIZEMOVE event is thrown, we will call 'mouseUp'.
			this.win.hookWindowMessage(WINAPI.WM_EXITSIZEMOVE, this.mouseUp.bind(this));
		} else if (!MainWindow.hookWindowMessageDisplayed && process.platform == "win32") {
			MainWindow.hookWindowMessageDisplayed = true;
			console.error(
				"BrowserWindow.hookWindowMessage is undefined.  Finsemble is running in an experimental state that will not be fully functional."
			);
		}

		/**
		 * Events that are triggered _before_ a resize or move event (allowing those events to be prevented).
		 */
		if (!this.useDOMMovement) {
			this.win.on("will-resize", (event, newBounds) => {
				this.userResizeEvent(event, newBounds);
				this.debouncedResizeHandler();
			});
			this.win.on("will-move", (event, newBounds) => {
				this.userMoveEvent(event, newBounds);
			});
		} else {
			this.win.on("will-resize", (event: ElectronEvent, newBounds: Rectangle) => {
				this.debouncedResizeHandler();
				this.handleWillResizeWithoutDOMMovement(event, newBounds);
			});
		}

		// Events that are thrown _after_ the window is moved or resized. Will occur on both user and system move/resize.
		// We only listen to these events to handle system actions.
		// We will treat these events the same way, the only difference being a changeType of 0 for move and 1 for resize.
		this.win.on("move", () => {
			this.moveOrResizeEvent(0);
		});
		this.win.on("resize", () => {
			this.debouncedResizeHandler();
			this.handleResizeEvent();
		});

		// Prevent the close event if another window has registered to be notified for `close-requested` on this window.
		this.win.on("close", (event) => {
			if (!this.closeRequested) {
				return;
			}
			logger.info("Blocking window close because of registered `close-requested` event");
			event.preventDefault();
			this.sendEventToWindows("close-requested", { name: this.windowName });
		});

		// When the window has finally closed, remove all of the listeners we created
		// and let the rest of the system know that we've gone down.
		this.win.on("closed", () => {
			this.onWindowClosed();
		});
	}

	/**
	 * Changes the 'closeRequested' boolean. This is a value that changes the way the window closes.
	 * If it is true, the window will wait for the program to do some work before closing the window.
	 * If it is false, nothing changes.
	 */
	closeRequestedAdd() {
		this.closeRequested = true;
	}

	closeRequestedRemove() {
		this.closeRequested = false;
	}

	/**
	 * Send a message to all windows. Right now, we don't know which windows are listening for which events so we send the events to all windows.
	 * @param {String} event
	 * @param {Object} data
	 */
	sendEventToWindows(event: string, data: any) {
		mainBus.sendEvent(`windowEvent-${this.windowName}.${event}`, data);
	}

	/**
	 * disabled-frame-bounds-changing requires a different format for bounds info
	 */
	getBoundsEventInfo() {
		if (!this.location) return;
		const mouseLocation = screen.getCursorScreenPoint();

		// Here we are using the mouse location + an offset because the bounds emitted by the will-move event
		// are wrong and cause the window to violently jerk in the general direction of the user's
		// mouse movement. This custom left/top enables smooth movement.
		const left = Number(mouseLocation.x) - Number(this.movingObj.mouseOffset.left);
		const top = Number(mouseLocation.y) - Number(this.movingObj.mouseOffset.top);
		const height = Number(this.location.height);
		const width = Number(this.location.width);
		return {
			left,
			top,
			width,
			height,
			right: left + width,
			bottom: top + height,
			changeType: 0,
			name: this.windowName,
		};
	}

	/**
	 * Debounce function for limiting events
	 * @param {*} func
	 * @param {*} delay
	 */
	debounce(func: any, delay: number) {
		let inDebounce: ReturnType<typeof setTimeout>;
		return () => {
			const context = this;
			const args = arguments;
			clearTimeout(inDebounce);
			inDebounce = setTimeout(() => func.apply(context, args), delay);
		};
	}

	/**
	 * Computes the edges that are being resized based on the current mouse and window locations if the window is located on 2 monitors with different scaling.
	 * In that case window bounds are based on the window primary monitor scaling, but mouse position has absolute values. For example
	 *
	 * | monitor1, scaling=2			| monitor2, scaling=1			|
	 * |--------------------------------|-------------------------------|
	 * |				window			|								|
	 * |				 _______________|________						|
	 * |				|				|		 |	resizing direction	|
	 * |				|				|		 |	==>					|
	 * |				|_______________|________|						|
	 * |								|								|
	 * |								|--------|----------------------|
	 * |								|	diff |
	 * |								|
	 * |								|
	 * |--------------------------------|
	 *
	 * Description of the example above:
	 * windowPrimaryDisplay = monitor1 (because the center of the window located on it)
	 * mouseDisplay = monitor2 (because the dragging by the right window edge)
	 * crossMonitorScale = 2 / 1
	 *
	 * For the right part of the window the mouse.X coordinate should be recalculated in the next way:
	 * 1. get difference between mouse.X and left border of the mouseDisplay
	 * 2. divide the difference (1) on crossMonitorScale to get the value if it were in the coordinates of windowPrimaryDisplay
	 * 3. sum the value (2) and left border of the mouseDisplay. That value is comparable with window.right edge
	 *
	 * 3 other cases with different monitors configuration are similar.
	 */
	computeResizeEdgesAcrossMultipleMonitors(mousePointerToEdgeTolerance: number) {
		const mouseDisplay = screen.getDisplayNearestPoint(this.startMoveMouseLocation);
		const windowMiddlePoint = {
			x: (this.startMoveWindowLocation.x + this.startMoveWindowLocation.right) / 2,
			y: (this.startMoveWindowLocation.y + this.startMoveWindowLocation.bottom) / 2,
		};
		const windowPrimaryDisplay = screen.getDisplayNearestPoint(windowMiddlePoint);
		// we should recalculate window bounds in case when the middle window point located on a different monitor then mouse
		const shouldRecalculate = windowPrimaryDisplay.id !== mouseDisplay.id;

		if (shouldRecalculate) {
			const recalculateLeftSide = this.startMoveWindowLocation.x < windowPrimaryDisplay.bounds.x;
			const recalculateRightSide =
				this.startMoveWindowLocation.right > windowPrimaryDisplay.bounds.x + windowPrimaryDisplay.bounds.width;
			const recalculateTopSide = this.startMoveWindowLocation.y < windowPrimaryDisplay.bounds.y;
			const recalculateBottomSide =
				this.startMoveWindowLocation.bottom > windowPrimaryDisplay.bounds.y + windowPrimaryDisplay.bounds.height;
			const crossMonitorScale = windowPrimaryDisplay.scaleFactor / mouseDisplay.scaleFactor;

			if (recalculateLeftSide && !resizeEdges.includes("left")) {
				const diff = windowPrimaryDisplay.bounds.x - this.startMoveMouseLocation.x;
				const newDiff = diff / crossMonitorScale;
				const fakeMouseX = mouseDisplay.bounds.x + mouseDisplay.bounds.width - newDiff;

				if (Math.abs(this.startMoveWindowLocation.x - fakeMouseX) < mousePointerToEdgeTolerance) {
					resizeEdges.push("left");
				}
			}

			// described in comments case
			if (recalculateRightSide && !resizeEdges.includes("right")) {
				const diff =
					this.startMoveMouseLocation.x - (windowPrimaryDisplay.bounds.x + windowPrimaryDisplay.bounds.width);
				const newDiff = diff / crossMonitorScale;
				const fakeMouseX = mouseDisplay.bounds.x + newDiff;

				if (Math.abs(fakeMouseX - this.startMoveWindowLocation.right) < mousePointerToEdgeTolerance) {
					resizeEdges.push("right");
				}
			}

			if (recalculateTopSide && !resizeEdges.includes("top")) {
				const diff = windowPrimaryDisplay.bounds.y - this.startMoveMouseLocation.y;
				const newDiff = diff / crossMonitorScale;
				const fakeMouseY = mouseDisplay.bounds.y + mouseDisplay.bounds.height - newDiff;

				if (Math.abs(this.startMoveWindowLocation.y - fakeMouseY) < mousePointerToEdgeTolerance) {
					resizeEdges.push("top");
				}
			}

			if (recalculateBottomSide && !resizeEdges.includes("bottom")) {
				const diff =
					this.startMoveMouseLocation.y - (windowPrimaryDisplay.bounds.y + windowPrimaryDisplay.bounds.height);
				const newDiff = diff / crossMonitorScale;
				const fakeMouseY = mouseDisplay.bounds.y + newDiff;

				if (Math.abs(fakeMouseY - this.startMoveWindowLocation.bottom) < mousePointerToEdgeTolerance) {
					resizeEdges.push("bottom");
				}
			}
		}
	}

	/**
	 * Computes the edges that are being resized based on the current mouse and window locations.
	 */
	computeResizeEdges() {
		// Electron resize thresholds:
		// https://github.com/electron/electron/blob/main/shell/browser/ui/views/frameless_view.cc#L19-L20
		const kResizeInsideBoundsSize = 5;
		const kResizeAreaCornerSize = 16;

		const windowTopLeftDisplay = screen.getDisplayNearestPoint(this.startMoveWindowLocation);
		const windowRightBottomDisplay = screen.getDisplayNearestPoint({
			x: this.startMoveWindowLocation.right,
			y: this.startMoveWindowLocation.bottom,
		});

		// the window located across 2 monitors with different scaling
		if (windowTopLeftDisplay.scaleFactor !== windowRightBottomDisplay.scaleFactor) {
			this.computeResizeEdgesAcrossMultipleMonitors(kResizeInsideBoundsSize);
		}

		// Calculate the distance from each edge
		const fromLeftEdge = Math.abs(this.startMoveMouseLocation.x - this.startMoveWindowLocation.x);
		const fromTopEdge = Math.abs(this.startMoveMouseLocation.y - this.startMoveWindowLocation.y);
		const fromRightEdge = Math.abs(this.startMoveMouseLocation.x - this.startMoveWindowLocation.right);
		const fromBottomEdge = Math.abs(this.startMoveMouseLocation.y - this.startMoveWindowLocation.bottom);

		// Check all SINGLE edges
		//
		// If the distance between the start mouse location and start window location in the x direction is less than 5 pixels
		if (fromLeftEdge <= kResizeInsideBoundsSize) {
			// we're resizing from the left
			resizeEdges.push("left");
		}
		//
		// If the distance between the start mouse location and start window location in the y direction is less than 5 pixels
		if (fromTopEdge <= kResizeInsideBoundsSize) {
			// we're resizing from the top
			resizeEdges.push("top");
		}
		//
		// If the distance between the start mouse location and the right edge of the start window location in the x direction is less than 5 pixels
		if (fromRightEdge <= kResizeInsideBoundsSize) {
			// we're resizing from the right
			resizeEdges.push("right");
		}
		//
		// If the distance between the start mouse location and the bottom edge of the start window location in the y direction is less than 5 pixels
		if (fromBottomEdge <= kResizeInsideBoundsSize) {
			// we're resizing from the bottom
			resizeEdges.push("bottom");
		}

		// Check all CORNERS
		// Corners are wacky. The top corners are defined as within 16 pixels from both the top edge and either side edge, but
		// the bottom corners are defined as 16 pixels from the side edge but only 5 pixels from the bottom edge. Consequently, we
		// have to account for these conditions in a few different "if" statements.
		//
		// NOTE: This would be easier if this code could look at the pointer icon, which would indicate if the resize icon were being used
		//
		// Within 16px from left & 16px from top
		if (fromLeftEdge <= kResizeAreaCornerSize && fromTopEdge <= kResizeAreaCornerSize) {
			resizeEdges.push("left");
			resizeEdges.push("top");
		}
		//
		// Within 16px from right & 16px from top
		if (fromRightEdge <= kResizeAreaCornerSize && fromTopEdge <= kResizeAreaCornerSize) {
			resizeEdges.push("right");
			resizeEdges.push("top");
		}
		//
		// Within 5px from left & 16px from bottom
		// OR
		// Within 5px from bottom & 16px from left
		if (
			(fromLeftEdge <= kResizeInsideBoundsSize && fromBottomEdge <= kResizeAreaCornerSize) ||
			(fromBottomEdge <= kResizeInsideBoundsSize && fromLeftEdge <= kResizeAreaCornerSize)
		) {
			resizeEdges.push("left");
			resizeEdges.push("bottom");
		}
		//
		// Within 5px from right & 16px from bottom
		// OR
		// Within 5px from bottom & 16px from right
		if (
			(fromRightEdge <= kResizeInsideBoundsSize && fromBottomEdge <= kResizeAreaCornerSize) ||
			(fromBottomEdge <= kResizeInsideBoundsSize && fromRightEdge <= kResizeAreaCornerSize)
		) {
			resizeEdges.push("right");
			resizeEdges.push("bottom");
		}

		// Remove duplicate entries from the array
		resizeEdges = [...new Set(resizeEdges)];
	}

	/**
	 * Adjusts the window bounds based on the edges being resized. Sometimes, specially
	 * in scaled monitors Electron moves other edges. In were we force the egdes not being
	 * moved to keep the original location when the movement started.
	 *
	 * @param {Object} newBounds - The new bounds of the window.
	 * @param {number} newBounds.x - The new x coordinate of the window.
	 * @param {number} newBounds.y - The new y coordinate of the window.
	 * @param {number} newBounds.right - The new right coordinate of the window.
	 * @param {number} newBounds.bottom - The new bottom coordinate of the window.
	 */
	adjustBounds(newBounds: any) {
		newBounds.right = newBounds.x + newBounds.width;
		newBounds.bottom = newBounds.y + newBounds.height;

		// Adjust x coordinate if left edge is not being resized
		if (!resizeEdges.includes("left")) {
			newBounds.x = this.startMoveWindowLocation.x;
		}

		// Adjust y coordinate if top edge is not being resized
		if (!resizeEdges.includes("top")) {
			newBounds.y = this.startMoveWindowLocation.y;
		}

		// Adjust right coordinate if right edge is not being resized
		if (!resizeEdges.includes("right")) {
			newBounds.right = this.startMoveWindowLocation.right;
		}

		// Adjust bottom coordinate if bottom edge is not being resized
		if (!resizeEdges.includes("bottom")) {
			newBounds.bottom = this.startMoveWindowLocation.bottom;
		}

		// Update height and width based on new bounds
		newBounds.height = newBounds.bottom - newBounds.y;
		newBounds.width = newBounds.right - newBounds.x;
	}

	private preventElectronDefaultResizeEventAndPrepareEventTimers(event: any) {
		// some other window is being resized. This one is likely being resized because it is docked.
		if (this.movable || (resizingWindow && resizingWindow != this.windowName)) {
			return;
		}

		event.preventDefault();
		this.isResizing = true;

		if (this.lastResizeTime && Date.now() - this.lastResizeTime < 10) {
			// Prevents a ton of resize requests from pummeling the system.
			return;
		}

		this.lastResizeTime = Date.now();
	}

	/**
	 * Handle resize events
	 * @param {WindowEvent} event
	 * @param {WindowBounds} newBounds
	 */
	resizeEventOsx(event: any, newBounds: any) {
		this.preventElectronDefaultResizeEventAndPrepareEventTimers(event);

		const winBounds = convertBoundsInfo(this.win.getBounds());

		this.lastSizeMoveLoc = {
			left: newBounds.x,
			top: newBounds.y,
			height: newBounds.height,
			width: newBounds.width,
			changeType: 1,
		};

		// Force non-moving edges to remain stationary
		const mousePos = screen.getCursorScreenPoint();

		if (
			winBounds.width != newBounds.width &&
			Math.abs(mousePos.x - winBounds.x) < Math.abs(mousePos.x - winBounds.right)
		) {
			// Moving left edge.  Force right to be stationary
			this.lastSizeMoveLoc.left = mousePos.x;
			this.lastSizeMoveLoc.width = Math.abs(mousePos.x - winBounds.right);
		}

		if (
			winBounds.height != newBounds.height &&
			Math.abs(mousePos.y - winBounds.bottom) < Math.abs(mousePos.y - winBounds.y)
		) {
			// Moving bottom edge.  Force top to be stationary
			this.lastSizeMoveLoc.top = winBounds.y;
			this.lastSizeMoveLoc.height = Math.abs(mousePos.y - winBounds.y);
		}

		this.sendEventToWindows("disabled-frame-bounds-changing", this.lastSizeMoveLoc);

		if (this.resizeEventTimeout) clearTimeout(this.resizeEventTimeout);
		this.resizeEventTimeout = setTimeout(() => {
			this.mouseUp();
		}, 250);
	}

	/**
	 * Handle resize events
	 * @param {WindowEvent} event
	 * @param {WindowBounds} newBounds
	 */
	userResizeEvent(event: any, newBounds: any) {
		this.preventElectronDefaultResizeEventAndPrepareEventTimers(event);
		this.computeResizeEdges();

		// If we aren't actually resizing the window, we've gotten into this event errantly. Instead of
		// sending out bad bounds-changing events, end here.
		if (!resizeEdges.length) return;

		this.adjustBounds(newBounds);

		this.lastSizeMoveLoc = {
			left: newBounds.x,
			top: newBounds.y,
			height: newBounds.height,
			width: newBounds.width,
			changeType: 1,
		};

		// This event will bubble up inside of the DockableWindow in finsemble. There, we will determine
		// whether the user's intended move will be allowed to proceed, or if we should modify it
		// (e.g., snap it to another window or monitor).
		this.sendEventToWindows("disabled-frame-bounds-changing", this.lastSizeMoveLoc);
	}

	/**
	 * Handles move or resize event that occurs after the user drops a window or the system moves a window
	 * We bubble up the same event for both types of moves to mimic the fin api
	 * Currently we only use this event for system moves.
	 * @param {*} event
	 */
	moveOrResizeEvent(changeType = 0) {
		/* Terry, no longer a problem
		// Electron sends two identical events for every move for some reason, only bubble up the first event
		const timestamp = Date.now();
		if (process.platform === "win32" && timestamp <= this.lastMove + 5) {
			return;
		}*/

		const bounds = this.win.getBounds();
		const convertedBounds = {
			left: bounds.x,
			top: bounds.y,
			height: bounds.height,
			width: bounds.width,
			changeType,
		};
		logger.verbose("move or resize event", this.windowName, JSON.stringify(convertedBounds));
		this.sendEventToWindows("bounds-changed", convertedBounds);
		this.lastMove = Date.now();
	}

	/**
	 * Handle will-move events while the user is moving a window
	 * @param {} event
	 */
	userMoveEvent(event: any, newBounds: any) {
		if (this.movable || (movingWindow && movingWindow != this.windowName)) {
			// some other window is the primary mover. This one is likely being dragged around because it is docked.
			// console.log("some other window is being moved: ", movingWindow);
			return;
		}

		event.preventDefault();

		const now = Date.now();
		if (!movingWindow) {
			movingWindow = this.windowName;
		} else if (this.lastResizeTime && now - this.lastResizeTime < 10) {
			// throttle
			logger.verbose(`Throttled move: Now: ${now}, lastResizeTime: ${this.lastResizeTime}`);
			return;
		}

		if (!this.isMoving) {
			this.isMoving = true;
		}

		const mouseLocation = screen.getCursorScreenPoint();

		// Here we are using the mouse location + an offset because the bounds emitted by the will-move event
		// are wrong and cause the window to violently jerk in the general direction of the user's
		// mouse movement. This custom left/top enables smooth movement.
		if (!this.useDOMMovement) {
			newBounds.x = Math.round(this.startMoveWindowLocation.x + mouseLocation.x - this.startMoveMouseLocation.x);
			newBounds.y = Math.round(this.startMoveWindowLocation.y + mouseLocation.y - this.startMoveMouseLocation.y);
		}

		if (this.startMoveMouseLocation && this.useDOMMovement) {
			newBounds.width = this.startMoveWindowLocation.width;
			newBounds.height = this.startMoveWindowLocation.height;
		}

		this.lastSizeMoveLoc = {
			left: newBounds.x,
			top: newBounds.y,
			height: newBounds.height,
			width: newBounds.width,
			changeType: 0,
		};

		// win7: fast mouse movement after mouse down sometimes has the mouse position
		// outside of the window bounds. Return to prevent cursor moving outside of window.
		if (
			mouseLocation.y < newBounds.y ||
			mouseLocation.y > newBounds.y + newBounds.height ||
			mouseLocation.x < newBounds.x ||
			mouseLocation.x > newBounds.x + newBounds.width
		) {
			return;
		}

		// uncomment the lines below for debugging.
		// console.log(this.startMoveWindowLocation, this.lastSizeMoveLoc);
		// console.log(this.lastSizeMoveLoc);

		// This event will bubble up inside of the DockableWindow in finsemble. There, we will determine
		// whether the user's intended move will be allowed to proceed, or if we should modify it
		// (e.g., snap it to another window or monitor).
		this.sendEventToWindows("disabled-frame-bounds-changing", this.lastSizeMoveLoc);
	}

	/**
	 * Track when the mouse is down so we know if the user initiated the move/resize event
	 * @param {WindowEvent} event
	 */
	mouseDown() {
		this.startMoveMouseLocation = screen.getCursorScreenPoint();
		this.startMoveWindowLocation = convertBoundsInfo(this.win.getBounds());
	}

	/**
	 * Handles mouseDown event captured from the DOM.
	 */
	windowMouseDown() {
		if (!this.useDOMMovement) return;
		if (!this.win.isDestroyed()) {
			this.mouseDown();
		}
	}

	/**
	 * Handles window moving event captured from the DOM and updates the window bounds according to where the window has moved.
	 * @param {WindowEvent} event DOM event
	 */
	windowMoving(event: any) {
		if (!this.useDOMMovement) return;
		const { mouseX, mouseY } = event.data;
		if (!this.win.isDestroyed()) {
			this.isMoving = true;
			// This is the absolute position of the current mouse (The origin (0,0) is the top left corner of the main display).
			const { x, y } = screen.getCursorScreenPoint();
			// This is the current window bounds in the format of {x, y, width, height}
			const newBounds = this.win.getBounds();

			const now = Date.now();
			if (!movingWindow) {
				movingWindow = this.windowName;
			} else if (this.lastResizeTime && now - this.lastResizeTime < 10) {
				// throttle
				logger.verbose(`Throttled move: Now: ${now}, lastResizeTime: ${this.lastResizeTime}`);
				return;
			}

			// lastSizeMoveLoc is the current window position.
			this.lastSizeMoveLoc = {
				left: x - mouseX,
				top: y - mouseY,
				height: newBounds.height,
				width: newBounds.width,
				changeType: 0,
			};
			this.sendEventToWindows("disabled-frame-bounds-changing", this.lastSizeMoveLoc);
		}
	}

	/**
	 * Updates the window bounds when the window stops moving.
	 */
	windowMoved() {
		if (!this.useDOMMovement) return;
		if (!this.win.isDestroyed()) {
			this.sendEventToWindows("disabled-frame-bounds-changed", this.getBounds());
			this.mouseUp();
		}
	}

	/**
	 * Handle the mouse up event. If we're moving/resizing send out events and reset the window
	 */
	mouseUp() {
		if (this.isMoving) {
			this.isMoving = false;
			this.sendEventToWindows("disabled-frame-bounds-changed", this.getBounds());
			movingWindow = null;
		}

		if (this.isResizing) {
			this.isResizing = false;
			this.sendEventToWindows("disabled-frame-bounds-changed", this.getBounds());
			resizingWindow = null;
			resizeEdges = [];
		}
	}

	/**
	 * Update the window options. Right now, actions only occur on four options
	 */
	updateOptions(eventObj: MainProcessBusEvent) {
		const { options } = eventObj.data;
		// permissions and securityPolicy are both protected options. The user cannot modify them once the window is created.
		if (Object.keys(options).length) {
			options.permissions = this.details.permissions;
			options.securityPolicy = this.details.securityPolicy;
		}
		const newAlwaysOnTopValue = options["alwaysOnTopLevel"] ?? this.defaultAlwaysOnTopLevel;
		for (const o in options) {
			switch (o) {
				case "opacity":
					/**
					 * Set opacity to the requested value, but not if the window is transparent.
					 * On VMWare, any toggle of opacity will cause the transparency to fail. Finsemble's
					 * window management service makes many calls to opacity, so catching the window
					 * state here is the most reliable way to ensure that transparency isn't inadvertently
					 * disabled through normal use.
					 */
					if (!this.myOpts.transparent) this.win.setOpacity(options[o]);
					break;
				case "alwaysOnTop":
					if (this.win.isAlwaysOnTop() !== newAlwaysOnTopValue) {
						this.win.setAlwaysOnTop(options[o], newAlwaysOnTopValue);
					}
					break;
				case "showTaskbarIcon":
					this.win.setSkipTaskbar(!options[o]);
					break;
				case "resizable":
					this.win.setResizable(options[o]);
					break;
				default:
					break;
			}
		}
		this.details = Object.assign(this.details, options);
		this.customData = Object.assign({}, this.customData, options.customData || {});
		eventObj.respond({ status: "success" });
	}

	/**
	 * Get a windows options.
	 */
	getDetails(eventObj: MainProcessBusEvent) {
		// For windows opened through window.open, the options passed to webContents.on('new-window')
		// include the webcontents itself, which fails serialization due to a circular reference to itself
		// Therefore, we shallow clone the options and delete the webcontents
		const detailsResponse = Object.assign({}, this.details);
		delete detailsResponse.webContents;
		detailsResponse.name = this.windowName;
		detailsResponse.customData = this.customData || {};
		logger.log("getDetails responding in Electron main process", this.windowName);
		eventObj.respond(detailsResponse);
	}

	/**
	 * Get a windows options.
	 */
	async getSnapshot(eventObj: MainProcessBusEvent) {
		// Get the bounds from the event data
		const requestedBounds = eventObj.data.area;

		// If bounds are provided, ensure they are well-formed
		if (!!requestedBounds && !validateBoundsCanBeWellFormed(requestedBounds)) {
			// If they are not well-formed, respond with an error and return early
			const message = `getSnapshot called with invalid bounds for area ${this.windowName}: ${JSON.stringify(eventObj.data.area)}`;
			logger.error(message);
			eventObj.respond({
				status: "failure",
				message,
			});

			return;
		}
		// If bounds are provided then ensure they have integer values
		let actualBounds = !!requestedBounds ? ensureBoundsHaveIntegerValues(requestedBounds) : requestedBounds;
		let _nativeImage = await this.win.capturePage(actualBounds);
		let pngImage = _nativeImage.toPNG();
		let base64String = pngImage.toString("base64");
		eventObj.respond(base64String);
	}

	/**
	 * Reloads views if any exist, otherwise reloads the window.
	 * This causes views to reload when the user presses ctrl+r on a window.open.
	 * The default behavior still also occurs, so the window is also reloaded with it's views.
	 */
	reload(eventObj: any) {
		const wasViewToReload = this.browserViews.reload();
		if (!wasViewToReload) this.win.reload();
		eventObj.respond({ status: "success" });
	}

	/**
	 * Responds to the showDeveloperTools event. Shows developer tools on the view if the window has a view, otherwise
	 * developer tools are opened on the window
	 */
	showDeveloperTools(eventObj: MainProcessBusEvent) {
		let { name, viewId, autoPick } = eventObj.data;
		if (autoPick) {
			const targetView = Object.values(this.browserViews.views).find((view) => view.webContentsName === name);
			const webContentsId = targetView?.id ?? false;
			if (webContentsId !== this.webContentsId) viewId = webContentsId;
		}
		if (viewId) {
			this.browserViews.showDeveloperTools(viewId);
		} else {
			if (this.devtools) {
				this.devtools.focus();
			} else {
				this.dontRedirectDevToolsOnce = true;
				const bw = launchDevtools(this.webContentsName, this.win.webContents, () => {
					this.devtools = null;
				});
				if (bw) this.devtools = bw;
			}
		}
		eventObj.respond({ status: "success" });
	}

	/**
	 * Invoked when the "devtools-opened" event is emitted for a MainWindow's WebContents.
	 *
	 * There is also an issue where the Chrome devtools open as a frame within small windows
	 * where the "detach" icon is inaccessible. Closing the devtools and then re-opening them
	 * explicitly into a dedicated window solves that issue.
	 *
	 * Also, if devtools are opened in a titlebar shell, then the developer probably really wants the devtools for the
	 * app contents (BrowserView). So this function also makes that switch.
	 *
	 * Note, this won't get called if an accelerator is pressed for a window that already has a devtools open.
	 * So, the accelerator will not focus an already open devtools window (whereas central logger will).
	 *
	 */
	onDevToolsOpened() {
		// Don't process the event if we've opened devtools explicitly via the API or central logger
		if (this.dontRedirectDevToolsOnce) {
			this.dontRedirectDevToolsOnce = false;
			return;
		}
		// Get the primary view (if any).
		const view = Object.values(this.browserViews.views)[0];

		// Choose either the primary BrowserView or this BrowserWindow
		const useView = this?.isTitlebarWindow && view;
		const which: any = useView ? view : this;
		const webContents = useView ? view.browserView.webContents : this.win.webContents;
		const { devtools, webContentsName } = which;

		// If we are meant to open the view then the main window's devtools will keep opening every time the
		// accelerator is pressed, so we have to keep closing them.
		if (webContents.id !== this.win.webContents.id) this.win.webContents.closeDevTools();

		// If the devtools is already open then just focus and return
		if (devtools) {
			devtools.focus();
			return;
		}

		// If the accelerator was pressed on a non-browserview Window (e.g. a UI component), then close the
		// auto-opened devtools now so that we can pop out our own.
		if (webContents.id === this.win.webContents.id) this.win.webContents.closeDevTools();

		// Close the devtools that was opened automatically by the "devtools-opened" event.
		this.win.webContents.closeDevTools();
		// Explicitly open devtools in a dedicated BrowserWindow
		const bw = launchDevtools(webContentsName, webContents, () => {
			which.devtools = null;
		});
		if (bw) which.devtools = bw;
	}

	/**
	 * Makes the window ignore all mouse events.
	 * If invalid parameters are supplied, the request is ignored.
	 *
	 * @param {MainProcessBusEvent} eventObj
	 * @param {object} eventObj.data
	 * @param {Boolean} eventObj.data.shouldIgnore Whether or not to ignore mouse events.
	 * @param {object|undefined} eventObj.data.options
	 * @param {Boolean} eventObj.data.options.forward Whether or not to send the mouse events to Chromium when ignored by window.
	 * @returns {undefined}
	 */
	setIgnoreMouseEvents(eventObj: MainProcessBusEvent) {
		const { ignore, options } = eventObj.data;
		const whenImproperlyConfigured = () => {
			logger.warn("setIgnoreMouseEvents was provided invalid parameters");
			eventObj.respond();
		};

		const isIgnoreNotBoolean = typeof ignore !== "boolean";
		if (isIgnoreNotBoolean === true) {
			whenImproperlyConfigured();
			return;
		}

		if (options) {
			const isOptionsNotObject = typeof options !== "object";
			if (isOptionsNotObject === true) {
				whenImproperlyConfigured();
				return;
			}

			if (options.hasOwnProperty("forward") === false || typeof options.forward !== "boolean") {
				whenImproperlyConfigured();
				return;
			}
		}

		this.win.setIgnoreMouseEvents(ignore, options);
		eventObj.respond();
	}

	/**
	 * Sets the minimum size of window
	 */
	setMinimumSize(eventObj: MainProcessBusEvent) {
		let { width, height } = eventObj.data.size;
		let currentMinSize = this.win.getMinimumSize();
		let bounds = this.win.getBounds();
		if (!width) {
			width = currentMinSize[0];
		}

		if (!height) {
			height = currentMinSize[1];
		}

		this.win.setMinimumSize(asInteger(width), asInteger(height));
		if (bounds.width < width || bounds.height < height) {
			bounds.width = width;
			bounds.height = height;
			this.win.setBounds(ensureBoundsHaveIntegerValues(bounds));
		}
		this.details.minWidth = width;
		this.details.minHeight = height;
		eventObj.respond(this.win.getMinimumSize());
		return this.win.getMinimumSize();
	}

	/**
	 * Sets the maximum size of window
	 */
	setMaximumSize(eventObj: MainProcessBusEvent) {
		let { width, height } = eventObj.data.size;
		let currentMaxSize = this.win.getMaximumSize();
		let bounds = this.win.getBounds();

		// for correct docking, the maximum width for toolbar should be default
		if (!width || this.windowName.startsWith("Toolbar")) {
			width = currentMaxSize[0];
		}

		if (!height) {
			height = currentMaxSize[1];
		}

		this.win.setMaximumSize(width, height);
		if (bounds.width > width || bounds.height > height) {
			bounds.width = width;
			bounds.height = height;
			this.win.setBounds(ensureBoundsHaveIntegerValues(bounds));
		}
		this.details.maxWidth = width;
		this.details.maxHeight = height;
		eventObj.respond(this.win.getMaximumSize());
		return this.win.getMaximumSize();
	}

	/**
	 * Set the bounds of a window
	 */
	setBounds(eventObj: MainProcessBusEvent) {
		const params = eventObj.data.bounds;
		if (!params) {
			logger.warn(`setBounds called with no bounds for window ${this.windowName}`);
			eventObj.respond({ status: "failure", message: `setBounds called with no bounds for window ${this.windowName}` });
			return;
		}
		if (this.isResizing) this.setBoundsCalled = true;
		const xMissing = !params.hasOwnProperty("x");
		const yMissing = !params.hasOwnProperty("y");
		const heightMissing = !params.hasOwnProperty("height");
		const widthMissing = !params.hasOwnProperty("width");

		if (xMissing || yMissing || widthMissing || heightMissing) {
			let errorMessage = "The following properties are missing from setBounds: ";
			if (xMissing) errorMessage = `${errorMessage}x, `;
			if (yMissing) errorMessage = `${errorMessage}y, `;
			if (widthMissing) errorMessage = `${errorMessage}width, `;
			if (heightMissing) errorMessage = `${errorMessage}height`;
			errorMessage = `${errorMessage}.`;
			logger.error(`${errorMessage} ${JSON.stringify(params)} `);
			eventObj.respond({ status: "failure", message: `${errorMessage} ${JSON.stringify(params)}` });
			return;
		}

		// The bounds must be integers, or else Electron will throw an exception.
		// We use a function that throws so that we can catch the error and respond to the caller.
		// Rounding is done inside ensureBoundsHaveIntegerValues.
		let ensuredBounds;

		try {
			ensuredBounds = ensureBoundsHaveIntegerValues(params);
		} catch (err) {
			logger.error(`setBounds called with invalid bounds for window ${this.windowName}: ${JSON.stringify(params)}`);
			eventObj.respond({
				status: "failure",
				message: `setBounds called with invalid bounds for window ${this.windowName}: ${JSON.stringify(params)}`,
			});
			return;
		}

		/**
		 * Hack. For some reason this must be called twice to work. It's almost like the first call didn't work.  One manifestation is when moving the toolbar around then bringing
		 * up a toolbar menu -- the menu would by displayed based on an old setBounds value.
		 */
		this.win.setBounds(ensuredBounds);

		this.location = params;
		this.win.setSize(ensuredBounds.width, ensuredBounds.height);

		// This is a workaround for an electron bug which leaves a white gap at the bottom of small windows like the toolbar. https://chartiq.kanbanize.com/ctrl_board/18/cards/12645/details/
		// setShape() is used to mask the white gap beyond the active portion of the window.
		const maybeMaskSmallWindow = () => {
			if (params.width > 56 && params.height > 56) {
				// This prevents the visible portion of the window from being stuck in that small state if a window starts out big and then is resized smaller than 56px high and then resized bigger again.
				this.win.setShape([]);
				return;
			}
			// Adjust for small window.
			// Electron throws an exception on bad input even though it is not documented.
			try {
				this.win.setShape([ensureBoundsHaveIntegerValues({ x: 0, y: 0, width: params.width, height: params.height })]);
			} catch (err) {
				logger.error(err);
				this.win.setShape([]);
			}
		};

		const cornerRounding = this.customData?.window?.options?.cornerRounding ?? true;
		// If Finsemble's setShape function hasn't been explicitly called then check and adjust for small window
		if (!cornerRounding && !this.partialShapeSet) maybeMaskSmallWindow();

		if (params.width > this.myOpts.maxWidth && this.myOpts.maxWidth > 0) {
			logger.error(
				`${this.windowName}: setBounds called with width: ${params.width} which is larger than the max of ${this.myOpts.maxWidth}.`
			);
		}
		if (params.height > this.myOpts.maxHeight && this.myOpts.maxHeight > 0) {
			logger.error(
				`${this.windowName}: setBounds called with height: ${params.height} which is larger than the max of ${this.myOpts.maxHeight}.`
			);
		}
		/*
		finsembleWindowService?.send("e2o.WindowEventStream", {
			eventName: "setBounds",
			windowName: this.windowName,
			open: true,
			bounds: this.location,
		} as PhysicalWindowState);
		*/

		eventObj.respond(this.getBounds());
		return this.getBounds();
	}

	/**
	 * Handles an incoming request to set the bounds for a view.
	 */
	setViewBounds(eventObj: any) {
		const { bounds, viewId } = eventObj.data;
		this.browserViews.setViewBounds(viewId, bounds);
		eventObj.respond({});
	}

	/**
	 * Brings view to front of other views in a window
	 */
	bringViewToFront(eventObj: any) {
		const { viewId } = eventObj.data;
		this.browserViews.bringViewToFront(viewId);
		eventObj.respond({});
	}

	showBrowserView(eventObj: any) {
		const { viewId } = eventObj.data;
		this.browserViews.showBrowserView(viewId);
		eventObj.respond({});
	}

	hideBrowserView(eventObj: any) {
		const { viewId } = eventObj.data;
		this.browserViews.hideBrowserView(viewId);
		eventObj.respond({});
	}

	/**
	 * Exposes Electron's setShape function which sets areas of a window that are visible and can be interacted with.
	 * These areas come in as an array of rectangles. Any areas not defined in the area will not be painted or clickable.
	 * Calling the function with no parameters or an empty array will reset the visible and clickable area to the entire window.
	 *
	 * Additionally, we set a local state to track if setShape has been called on only part of the window.
	 * To work around a small window bug, our setBounds function has it's own call to setShape for the full bounds of the window.
	 * We don't want to call that code if the intent was to only have part of the window be visible and interactable.
	 * @param {*} eventObj
	 */
	setShape(eventObj: any) {
		let rectArray;
		if (eventObj.data) {
			rectArray = eventObj.data.shapeArray || [];
		} else {
			rectArray = [];
		}

		this.win.setShape(rectArray.forEach((rect: any) => ensureBoundsHaveIntegerValues(rect)));

		let bounds = this.win.getBounds();
		// Set a state for if setShape was called on a part of the window or the entire window
		if (rectArray.length === 0) {
			this.partialShapeSet = false;
		} else {
			// Check if the setShape call was for the entire bounds of the window. Note we only check the first rectangle in the array.
			// It's possible there could be multiple rectangles that end up totalling the entire window size. That case is not handled here.
			this.partialShapeSet = rectArray[0].width < bounds.width || rectArray[0].height < bounds.height;
		}
		eventObj.respond();
	}

	/**
	 * Exposes Electron's setResizable function which sets whether the window is resizable by the user.
	 *
	 * @param {*} eventObj the event object with a data property that is a boolean indicating whether the window should be resizable.
	 */
	setResizable(eventObj: any) {
		if (eventObj.data) {
			this.win.setResizable(true);
		} else {
			this.win.setResizable(false);
		}
	}

	/**
	 * Set the window (taskbar) icon from a given url
	 * @param url The absolute path url of the icon image
	 */
	async setWindowIcon(url: string) {
		if (typeof url !== "string") {
			logger.error(`setWindowIcon() received invalid url "${url}" for window ${this.myOpts.name}`);
			return;
		}
		const { err, data } = await downloadGoLang(url, { logger });
		if (err || !data) {
			logger.warn(`Failed to download icon ${url}`, err);
			return;
		}

		const image = nativeImage.createFromBuffer(data.data);
		this.win.setIcon(image);
	}

	/**
	 * Set taskbar icon image
	 * @param {*} eventObj
	 */
	async setTaskbarIcon(eventObj: any) {
		const { iconURL } = eventObj.data;
		if (iconURL) {
			this.setWindowIcon(iconURL);
		} else {
			console.error("setTaskbarIcon missing iconURL");
		}

		eventObj.respond();
	}

	/**
	 * Set taskbar icon overlay image (e.g. for notifications)
	 * @param eventObj
	 */
	async setTaskbarIconOverlay(eventObj: any) {
		const { iconURL, description } = eventObj.data;
		if (typeof iconURL === "string") {
			const { err, data } = await downloadGoLang(iconURL, { logger });
			if (err || !data) {
				logger.warn(`Failed to download icon overlay ${iconURL}`, err);
				return;
			}

			const image = nativeImage.createFromBuffer(data.data);

			this.win.setOverlayIcon(image, description ?? "");
		} else {
			this.win.setOverlayIcon(null, "");
		}

		eventObj.respond();
	}

	/**
	 * Set the title displayed in the taskbar
	 * @param {*} eventObj the event object
	 */
	async setTaskbarTitle(eventObj: any) {
		const { title } = eventObj.data;

		if (title) {
			this.win.setTitle(title);
		}
	}

	/**
	 * Open a native file selection dialog on a window
	 *
	 * @param {Object} params - see https://www.electronjs.org/docs/api/dialog
	 */
	async showNativeOpenDialog(eventObj: any) {
		const options = eventObj.data;

		dialog
			.showOpenDialog(this.win, options)
			.then(({ canceled, filePaths }) => {
				eventObj.respond({
					error: null,
					canceled,
					filePaths,
				});
			})
			.catch((e) => {
				eventObj.respond({
					error: e.message,
					canceled: false,
					filePaths: [],
				});
			});
	}

	/**
	 * Gets the window bounds from the system. Used to determine where the system moved the window after a monitor change
	 * getBounds returns the last location as Fea knows it instead of asking the system. Test if using the last location is really necessary to see if this function can be merged with getBounds.
	 */
	getBoundsFromSystem(eventObj: MainProcessBusEvent) {
		const bounds = this.win.getBounds() || this.lastSizeMoveLoc;
		const convertedBounds = {
			left: bounds.x,
			top: bounds.y,
			height: bounds.height,
			width: bounds.width,
			bottom: bounds.y + bounds.height,
			right: bounds.x + bounds.width,
		};

		if (eventObj) eventObj.respond(convertedBounds);
		return convertedBounds;
	}

	/**
	 * Get the bounds of a window
	 */
	getBounds(eventObj?: MainProcessBusEvent) {
		// bounds is either that, or the lastMoveLoc. This smells like code that was necessary
		// to get something working quickly. We should determine in which scenarios these things
		// may happen.
		let bounds = convertBoundsInfo(this.location || this.win.getBounds());
		bounds = bounds || this.lastSizeMoveLoc;
		if (eventObj) eventObj.respond(bounds);
		return bounds;
	}

	/**
	 * Get the title displayed in the taskbar
	 * @param eventObj
	 * @returns {string} window title
	 */
	async getTaskbarTitle(eventObj?: MainProcessBusEvent) {
		const title = this.win.getTitle();
		if (eventObj) eventObj.respond(title);
		return title;
	}

	/**
	 * Show a window at a specific location. The show here does not set focus.
	 */
	showAt(eventObj: MainProcessBusEvent) {
		const params = eventObj.data;
		if (!this.win) return;
		logger.debug(`Showing window ${this.windowName} at (${params.top}, ${params.left})`);
		try {
			this.win.setPosition(asInteger(params.left), asInteger(params.top));
			this.win.showInactive();
			this.isWindowShowing = true;
			eventObj.respond({ status: "success" });
		} catch (e: any) {
			logger.error(e.message);
			eventObj.respond({ status: "failure", message: e.message });
		}
	}

	/**
	 * Is a window visible
	 */
	isShowing(eventObj: MainProcessBusEvent) {
		eventObj.respond(this.isWindowShowing);
	}

	/**
	 * Show a window. This will set focus
	 */
	show(eventObj: MainProcessBusEvent) {
		logger.debug(`Showing window ${this.windowName}.`);
		this.win.showInactive();
		this.isWindowShowing = true;
		eventObj.respond({ status: "success" });
	}

	/**
	 * Hide the window
	 */
	hide(eventObj: MainProcessBusEvent) {
		logger.debug(`Hiding window ${this.windowName}.`);
		this.win.hide();
		this.isWindowShowing = false;
		eventObj.respond({ status: "success" });
	}

	/**
	 * Focus the window or browser view this is a title bar shell
	 */
	focus(eventObj: MainProcessBusEvent) {
		const params = eventObj.data;
		logger.debug(`Focusing window ${this.windowName}.`);
		if (this.isTitlebarWindow && params?.titleBarWindow !== true) {
			this.browserViews.focus();
		} else {
			this.win.focus();
		}
		eventObj.respond({ status: "success" });
	}

	/**
	 * Blur the window
	 */
	blur(eventObj: MainProcessBusEvent) {
		logger.debug(`Blurring window ${this.windowName}.`);
		this.win.blur();
		eventObj.respond({ status: "success" });
	}

	/**
	 * Close the window.
	 */
	async close(eventObj?: MainProcessBusEvent) {
		this.logLifecycleStatus("Close", `Close initiated via the API.`);
		if (eventObj?.data?.force === true) {
			// Make sure we don't prevent Electron from closing the window when forcing close.
			this.closeRequested = false;
		}
		let response: any = null;
		if (!this.win)
			logger.error(
				`${this.lifecycleLogPreface(
					"Close"
				)} Attempting to close a MainWindow that has no BrowserWindow. This shouldn't be possible.`
			);
		// There is another 'closed' handler that handles alt+f4 and other out of bounds closes.
		// In that case, we want it to do nothing, as we'll handle all of that responsibility in `afterWindowCloses`
		this.wasClosedByRender = true;
		try {
			// After the window closes, let the render process know that the close is complete.
			// this will also be invoked after `win.destroy()`, if the timeout fires.
			const afterWindowCloses = () => {
				this.logLifecycleStatus("Close.afterWindowClose", `event received from Electron or close timed out.`);
				// this.win.removeEventListener("closed", afterWindowCloses);
				this.closeCleanup();
				response = { status: "success" };
				this.logLifecycleStatus("Close", `Close complete for FEA. Sending response to Finsemble.`);
				eventObj?.respond(response);
			};

			this.win.on("closed", afterWindowCloses);
			this.logLifecycleStatus("Close", `Close method being invoked.`);
			// Close all views before closing the window.
			await this.browserViews.removeAll();
			/**
			 * "unload", "beforeunloads" and "close" events won't emit if win.destroy()
			 * is called. Since Finsemble depends on the "unload" event, we call
			 * win.close() to give the window the chance to close gracefully.
			 */
			this.logLifecycleStatus("Close", `Closing browser window gracefully.`);
			this.win.close();
			/**
			 * If win.close() doesn't manage to close the window within the specified
			 * duration then call win.destroy() to forcefully terminate the window.
			 */
			this.destroyWindowTimer = this.destroyWindow(30000, afterWindowCloses);
		} catch (e: any) {
			logger.error(
				`${this.lifecycleLogPreface("Close")} Close failed. Will still respond to finsemble. Error: ${e.message}`
			);
			this.closeCleanup();
			response = { status: "failure" };
			eventObj?.respond(response);
		}
	}

	/**
	 * Destroys the window after a specified duration
	 * @param {number} duration The duration in milliseconds
	 * @private
	 */
	destroyWindow(duration: number, cb = Function.prototype) {
		return setTimeout(() => {
			logger.error(
				`${this.lifecycleLogPreface(
					"Close.destroyWindow"
				)} Window close failed to complete within ${duration} milliseconds. Attempting to destroy window.`
			);
			if (this.win && !this.win.isDestroyed()) {
				try {
					this.win.destroy();
					this.logLifecycleStatus("Close.destroyWindow", `Window destroy successful.`);
					cb();
				} catch (err: any) {
					logger.error(
						`${this.lifecycleLogPreface("Close.destroyWindow")} Window destroy failure. Error message: ${
							err.message
						}. Will still tell Finsemble that close is complete.`
					);
					cb();
				}
			} else {
				cb();
			}
		}, duration);
	}

	/**
	 * Maximize the window
	 */
	maximize(eventObj: MainProcessBusEvent) {
		if (this.win.isMaximized()) return;
		this.inMaximizeAnimation = true;
		logger.debug(`Maximizing window ${this.windowName}`);
		this.win.maximize();
		eventObj.respond({ status: "success" });
	}

	/**
	 * Minimize the window
	 */
	minimize(eventObj: MainProcessBusEvent) {
		logger.debug(`Minimizing window ${this.windowName}.`);
		this.isWindowShowing = false;
		this.win.minimize();
		eventObj.respond({ status: "success" });
	}

	/**
	 * Event handler for out-of-bounds-closes (e.g., alt+f4).
	 * If the window is closed but it didn't go through the `fin` API, we will call `closeCleanup`.
	 */
	onWindowClosed() {
		this.logLifecycleStatus("Close.onWindowClose", `Closed Event received from electron.`);
		if (!this.wasClosedByRender) {
			this.logLifecycleStatus("Close.onWindowClose", `Cleaning up.`);
			this.closeCleanup();
		} else {
			this.logLifecycleStatus(
				"Close",
				`Closed event was triggered from a request from the render method. Deferring cleanup.`
			);
		}
	}

	/**
	 * All of the cleanup methods that need to be run when the window is finally closed.
	 */
	closeCleanup() {
		this.logLifecycleStatus("Close", `closeCleanup started.`);
		delete windowsIndex.byWebContentsId[this.webContentsId];
		delete windowsIndex.byWindowName[this.windowName];
		delete windowsIndex.byWebContentsName[this.webContentsName];

		// don't leave devtools hanging open.
		if (this.devtools) {
			this.devtools.close();
		}

		iab.unsubscribeWebContentsId(this.webContentsId);
		mainBus.unsubscribeWebContentsId(this.webContentsId);
		this.browserViews.closeCleanup();

		//clean up the hack to reset the zoom interval when the window closes
		if (this.zoomInterval) clearInterval(this.zoomInterval);
		// This is a method passed in when the window is created.
		// Get rid of all subscribers so if a window with the same name is reincarnated, FEA won't blow up.
		this.logLifecycleStatus("Close", `Removing MainBus subscriptions.`);
		this.logLifecycleStatus("Close", `Removing API handlers.`);
		this.removeAPIHandlers();
		this.logLifecycleStatus("Close", `Sending a 'closed' event to finsemble.`);
		this.sendEventToWindows("closed", { name: this.windowName });
		// Since win.close() was successful, we don't need to destroy the window
		this.logLifecycleStatus("Close", `Removing destroyWindowTimer.`);
		if (this.destroyWindowTimer) clearTimeout(this.destroyWindowTimer);
		this.logLifecycleStatus("Close", `closeCleanup completed.`);
		if (this.win) this.win.removeAllListeners();
	}

	/**
	 * Helper function to log a lifecycle message. Helps standardize log look and feel.
	 * @param {} lifecycleMoment The 'moment' we're in. E.g., window creation, close.
	 * @param {*} message Message to log.
	 */
	logLifecycleStatus(lifecycleMoment: any, message: any) {
		logger.log(`${this.lifecycleLogPreface(lifecycleMoment)} ${message}`);
	}

	/**
	 * Makes a consistent preface to a log message. Handy helper function.
	 * @param {*} lifecycleMoment
	 */
	lifecycleLogPreface(lifecycleMoment: any) {
		const lifecycleNotice = `WINDOW LIFECYCLE NOTICE. MOMENT: ${lifecycleMoment}`;
		const windowName = `Window Name: ${this.windowName}`;
		return `${lifecycleNotice} ${windowName}`;
	}

	/**
	 * Sets the window to always on top. This will bring it to the top of the stack of windows.
	 * Immediately afterwards, changes alwaysOnTop to false. This allows other windows to sit on top of it
	 * when they're focused. BringToFront isn't actually an electron method.
	 * **NOTE**: This only happens if the window is not 'alwaysOnTop'. If the window is always on top, it's already
	 * brought to front.
	 */
	bringToFront(eventObj: MainProcessBusEvent) {
		logger.debug(`Bringing window ${this.windowName} to front.`);

		if (!this.myOpts.alwaysOnTop && this.isWindowShowing) {
			/**
			 * **NOTE**: Electron's moveTop() will not give a window focus if the entire Electron app itself
			 * does not have focus at the operating system level. Flipping alwaysOnTop is the workaround.
			 * @see https://github.com/electron/electron/pull/15358
			 *
			 * **Also note**: "normal" is required, otherwise a window will be sent to the back during screen share
			 *
			 * The third parameter (relativeLevel) is necessary to bring the window to the front on macOS
			 */
			this.win.setAlwaysOnTop(true, "normal", 1);
			this.win.setAlwaysOnTop(false);
			//this.win.moveTop();
		}
		eventObj.respond({ status: "success" });
	}

	/**
	 * Restore a window from minimize or maximize.
	 */
	restore(eventObj: MainProcessBusEvent) {
		logger.debug(`Restoring window ${this.windowName} to front.`);
		// A window that is minimized from a maximized state will have both isMaximized() and isMinimized() true!
		// Note that isMaximized() will be true (on Mac only?) if the window is sized as if it was maximized
		if (this.win.isMaximized() && !this.win.isMinimized()) {
			this.inMaximizeAnimation = true;
			this.isRestoring = true;
			const normalBounds = this.win.getNormalBounds();

			this.win.unmaximize();
			// This set bounds shouldn't be necessary (unmaximize should restore to normal bounds), but if this isn't
			// done, then the window ends up in the wrong location.
			//this.win.setBounds(normalBounds);
			this.location = normalBounds;
		} else {
			this.win.restore();
			this.location = this.win.getBounds();
		}

		this.isWindowShowing = true;
		eventObj.respond({ status: "success" });
	}

	/**
	 * Disable a windows frame. This makes it so a user cannot move the window. Movement must occur from setBounds while this is active. While disabled, window will emit `disabled-frame-bounds-changing` events.
	 */
	disableFrame(eventObj: MainProcessBusEvent) {
		logger.debug(`${this.lifecycleLogPreface("disableFrame")} Disabling frame.`);
		this.movable = false;
		if (!this.win) {
			logger.warn(`DisableFrame failed because window is not defined: ${this.windowName} `);
			return;
		}
		eventObj.respond({ status: "success" });
	}

	/**
	 * Sets the window back to user moveable
	 */
	enableFrame(eventObj: MainProcessBusEvent) {
		logger.debug(`${this.lifecycleLogPreface("enableFrame")} Enabling frame.`);
		this.movable = true;
		eventObj.respond({ status: "success" });
	}

	/**
	 * Remove a BrowserView from the window and close it
	 */
	async removeBrowserView(eventObj: MainProcessBusEvent) {
		const { viewId } = eventObj.data;
		const didRemoveBrowserView = await this.browserViews.removeBrowserView(viewId);
		eventObj.respond({ status: didRemoveBrowserView ? "success" : "fail" });
	}

	/**
	 * Creates a new BrowserView in the window.
	 */
	async addBrowserView(eventObj: MainProcessBusEvent) {
		let params = eventObj.data;
		const id = await this.browserViews.addBrowserView(params);
		eventObj.respond({ id });
	}

	setZoomFactor(eventObj: MainProcessBusEvent) {
		const { zoomFactor } = eventObj.data;

		if (this.isTitlebarWindow) {
			this.browserViews.setZoomFactor(zoomFactor);
		} else {
			this.win.webContents.setZoomFactor(zoomFactor);
		}

		eventObj.respond({ status: "success" });
	}

	getZoomFactor(eventObj: MainProcessBusEvent) {
		let zoomFactor;
		if (this.isTitlebarWindow) {
			zoomFactor = this.browserViews.getZoomFactor();
		} else {
			zoomFactor = this.win.webContents.getZoomFactor();
		}
		eventObj.respond({ zoomFactor, status: "success" });
	}

	setZoomLevel(eventObj: MainProcessBusEvent) {
		const { zoomLevel } = eventObj.data;

		if (this.isTitlebarWindow) {
			this.browserViews.setZoomLevel(zoomLevel);
		} else {
			this.win.webContents.setZoomLevel(zoomLevel);
		}
		eventObj.respond({ status: "success" });
	}

	getZoomLevel(eventObj: MainProcessBusEvent) {
		let zoomLevel;
		if (this.isTitlebarWindow) {
			zoomLevel = this.browserViews.getZoomLevel();
		} else {
			zoomLevel = this.win.webContents.getZoomLevel();
		}
		eventObj.respond({ zoomLevel, status: "success" });
	}
}
